import React, { Component, Fragment } from 'react';
import { Row, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import 'react-toggle/style.css';
import moment from 'moment';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import DraggableField from './draggableField';
import DraggableCustomField from './draggableCustomField';

import RichTextEditor from '../../views/pages/components/richTextEditor';
import InputField from '../inputs/inputField';
import SelectField from '../inputs/selectField';
import ToggleField from '../inputs/toggleField';
import AddCustomFieldModal from '../../sections/event/customFields/addCustomFieldModal';
import UploadOptionsModal from './uploadOptionsModal';
import Joyride from 'react-joyride';
import tutorials from '../../layouts/components/tutorials';
import LoadingWizard from '../spinner/wizard';
import API from '../../utils/API';

const reorder = (list, startIndex, endIndex) => {
	const result = Array.from(list);
	const [removed] = result.splice(startIndex, 1);
	result.splice(endIndex, 0, removed);
	return result;
};

const groupVersionsByDate = (versions) => {
	const now = moment();
	const groups = {};

	versions.forEach((version) => {
		const versionDate = moment(version.createdAt);
		let groupKey, headerText;

		if (!version.createdAt) {
			groupKey = 'unknown';
			headerText = 'Unknown Date';
		} else if (versionDate.isSame(now, 'week')) {
			// Same week - group by day
			groupKey = `week-${versionDate.format('dddd')}`;
			headerText = versionDate.format('dddd');
		} else if (versionDate.isSame(now.clone().subtract(1, 'week'), 'week')) {
			// Last week
			groupKey = 'last-week';
			headerText = 'Last Week';
		} else {
			// Older - group by month/year
			const isCurrentYear = versionDate.isSame(now, 'year');
			if (isCurrentYear) {
				groupKey = `month-${versionDate.format('MMMM')}`;
				headerText = versionDate.format('MMMM');
			} else {
				groupKey = `month-year-${versionDate.format('MMMM-YYYY')}`;
				headerText = versionDate.format('MMMM YYYY');
			}
		}

		if (!groups[groupKey]) {
			groups[groupKey] = {
				header: headerText,
				versions: [],
				sortKey: versionDate.valueOf()
			};
		}
		groups[groupKey].versions.push(version);
	});

	// Sort versions within each group by creation date (newest first)
	Object.keys(groups).forEach(key => {
		groups[key].versions.sort((a, b) => moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf());
	});

	// Sort groups by date (newest first)
	const sortedKeys = Object.keys(groups).sort((a, b) => {
		return groups[b].sortKey - groups[a].sortKey;
	});

	return { groups, sortedKeys };
};

const fieldTypes = [
	{ value: 'text', label: 'Text' },
	{ value: 'email', label: 'Email' },
	{ value: 'phone', label: 'Phone' },
	{ value: 'phoneNumber', label: 'Phone Number' },
	{ value: 'number', label: 'Number' },
	{ value: 'rating', label: 'Rating' },
	{ value: 'multipleCheck', label: 'Multiple Check' },
	{ value: 'textarea', label: 'Text Area' },
	{ value: 'checkbox', label: 'Checkbox' },
	{ value: 'select', label: 'Select' },
	{ value: 'list', label: 'List' },
	{ value: 'nestedSections', label: 'Nested Sections' },
	{ value: 'inventory', label: 'Inventory Item' },
	{ value: 'color', label: 'Color' },
	{ value: 'date', label: 'Date' },
	{ value: 'se-date', label: 'Simple Date' },
	{ value: 'datetime-local', label: 'Date Time Local' },
	{ value: 'file', label: 'File' },
	{ value: 'hidden', label: 'Hidden' },
	{ value: 'month', label: 'Month' },
	{ value: 'password', label: 'Password' },
	{ value: 'radio', label: 'Radio' },
	{ value: 'range', label: 'Range' },
	{ value: 'search', label: 'Search' },
	{ value: 'submit', label: 'Submit' },
	{ value: 'time', label: 'Time' },
	{ value: 'url', label: 'URL' },
	{ value: 'week', label: 'Week' },
	{ value: 'image', label: 'Image' },
	{ value: 'medProLookup', label: 'MedPro Look up' },
	{ value: 'salesForceSearchContact', label: 'Salesforce Search Contact' },
	{ value: 'salesForceSearchAccount', label: 'Salesforce Search Account' },
	{ value: 'notifierEmail', label: 'Notifier Email' },
	{ value: 'groupSelection', label: 'Group Selection' },
	{ value: 'redirectToPage', label: 'Redirect to Page' },
	{ value: 'inventoryLookup', label: 'Inventory Lookup' },
	{ value: 'singleInventoryRedemption', label: 'Single Inventory Redemption' },
	{ value: 'addressLookup', label: 'Address Lookup' },

];

const unattachedFieldList = [
	{
		_id: 'unattached',
		title: 'Text Components',
		fields: [
			{
				_id: '*text',
				fieldName: 'Text',
				fieldType: 'text',
				required: false,
				disabled: false,
				propertyType: 'custom',
				icon: <i className="las la-i-cursor" />
			},
			{
				_id: '*email',
				fieldName: 'Email',
				fieldType: 'email',
				required: false,
				disabled: false,
				propertyType: 'custom',
				icon: <i className="las la-at" />
			},
			{
				_id: '*phone',
				fieldName: 'Phone Number',
				fieldType: 'tel',
				required: false,
				disabled: false,
				propertyType: 'custom',
				icon: <i className="las la-phone" />
			},
			{
				_id: '*number',
				fieldType: 'number',
				fieldName: 'Number',
				required: false,
				disabled: false,
				icon: <i className="las la-list-ol" />,
				moduleConfiguration: {
					allowNegative: false,
				}
			},
			{ _id: '*textarea', fieldType: 'textarea', fieldName: 'Text Area', required: false, disabled: false, }
		]
	},
	{
		_id: 'unattached2',
		title: 'Date & Time Components',
		fields: [
			{ _id: '*date', fieldType: 'date', fieldName: 'Date', required: false, disabled: false, },
			{ _id: '*se-date', fieldType: 'se-date', fieldName: 'Simple Date', required: false, disabled: false, },
			{ _id: '*datetime-local', fieldType: 'datetime-local', fieldName: 'Date Time Local', required: false, disabled: false, },
			{ _id: '*month', fieldType: 'month', fieldName: 'Month', required: false, disabled: false, },
			{ _id: '*time', fieldType: 'time', fieldName: 'Time', required: false, disabled: false, },
			{ _id: '*week', fieldType: 'week', fieldName: 'Week', required: false, disabled: false, }
		]
	},
	{
		_id: 'unattached3',
		title: 'List Components',
		fields: [
			{ _id: '*select', fieldType: 'select', fieldName: 'Select', required: false, disabled: false, },
			{ _id: '*color', fieldType: 'color', fieldName: 'Color', required: false, disabled: false, },
			{ _id: '*radio', fieldType: 'radio', fieldName: 'Radio', required: false, disabled: false, },
			{ _id: '*checkbox', fieldType: 'checkbox', fieldName: 'Checkbox', required: false, disabled: false, },
			{ _id: '*multipleCheck', fieldType: 'multipleCheck', fieldName: 'Multiple Check', required: false, disabled: false, },
			{ _id: '*nestedSections', fieldType: 'nestedSections', fieldName: 'Nested Sections', required: false, disabled: false, }
		]
	},
	{
		_id: 'unattached4',
		title: 'Additional Components',
		fields: [
			{ _id: '*file', fieldType: 'file', fieldName: 'File', required: false, disabled: false, },
			{ _id: '*hidden', fieldType: 'hidden', fieldName: 'Hidden', required: false, disabled: false, },
			{ _id: '*password', fieldType: 'password', fieldName: 'Password', required: false, disabled: false, },
			{ _id: '*range', fieldType: 'range', fieldName: 'Range', required: false, disabled: false, },
			{ _id: '*search', fieldType: 'search', fieldName: 'Search', required: false, disabled: false, },
			{ _id: '*url', fieldType: 'url', fieldName: 'URL', required: false, disabled: false, },
		]
	},
	{
		_id: 'unattached5',
		title: 'Special Components',
		fields: [
			{
				_id: '*phoneNumber',
				fieldType: 'phoneNumber',
				fieldName: 'Phone Number',
				required: false,
				disabled: false,
				moduleConfiguration: {
					enableInternational: false
				}
			},
			{
				_id: '*addressLookup',
				fieldType: 'addressLookup',
				fieldName: 'Address Lookup',
				required: false,
				disabled: false,
				moduleConfiguration: {
					relatedFields: {
						address: '',
						address2: '',
						city: '',
						state: '',
						zip: '',
						country: ''
					}
				}
			},
		]
	}
];

const columnSizeOptions = [
	{ value: 'col-12', label: 'Full Width' },
	{ value: 'col-6', label: 'Half Width' },
	{ value: 'col-4', label: 'Quarter Width' },
	{ value: 'col-3', label: 'Third Width' },
	{ value: 'col-2', label: 'One-Third Width' }
];

const hiddenAttendeeFieldTypes = ['medProLookup', 'additionalAttendees'];

class FormBuilder extends Component {
	constructor(props) {
		super(props);
		this.state = {
			editedFields: props.fields,
			customFields: this.props.fields.map((category) => {
				const title = category.title;
				const headers = category.fields.map((header) => {
					const name = header.fieldName;
					return {
						_id: header._id,
						category: title,
						categoryId: category._id,
						value: name,
						label: name
					};
				});
				return {
					_id: category._id,
					label: title,
					value: category._id,
					options: headers
				};
			}),
			fieldModal: false,
			adding: false,
			loading: true,
			editing: false,
			removing: false,
			removedCategoryId: 0,
			removedFieldId: 0,
			customFieldsCollapsed: false,
			unattachedFieldsCollapsed: true,
			formButtonsCollapsed: true,
			fieldTypeTabSelected: 0,
			currentPage: 0,
			formEditorTabSelected: 0,
			selectedSection: 0,
			addCustomFieldModalOpen: false,
			detailsExpanded: false,
			detailsOpen: true,
			componentLibraryIsOpen: false,
			focusedFieldIndex: null,
			focusedFieldId: null,
			runPageTutorial: false,
			uploadOptionsModalOpen: false,
			uploadTargetSection: null,
			uploadTargetField: null,
			historyOpen: false,
			versions: [],
			versionsLoading: false,
			previewedVersionId: null,
			hasPendingEdits: false,
			renderOnly: false,
			savedFormBeforeHistory: null
		};
		this.onDragEnd = this.onDragEnd.bind(this);
	}

	selectField = (index) => {
		this.setState({ selectedField: index, fieldSelected: true });
	};

	onDragEnd(result) {
		// Prevent drag operations when in read-only mode
		if (this.state.renderOnly) {
			return;
		}
		// dropped outside the list
		const { focusedFieldId } = this.state;
		if (!result.destination) {
			return;
		}
		const { currentPage } = this.state;

		if (result.type === 'field') {
			const updatedForm = this.props.editedForm;
			const sections = updatedForm.pages[currentPage].sections;

			const droppedInSectionIndex = sections.findIndex((sec) => sec._id == result.destination.droppableId);
			if (droppedInSectionIndex > -1) {
				const draggedField = result.source.index;
				if (result.destination.droppableId == result.source.droppableId) {
					sections[droppedInSectionIndex].fields = reorder(
						sections[droppedInSectionIndex].fields,
						draggedField,
						result.destination.index
					);
				} else if (
					result.source.droppableId === 'drop-custom-fields' ||
					result.source.droppableId === 'drop-attendee-fields'
				) {
					const nextIndex = sections[droppedInSectionIndex].fields.length;
					if (draggedField.propertyType === 'base') {
						draggedField._id = `${draggedField._id}`;
					}
					sections[droppedInSectionIndex].fields.push({
						columnClass: 'col-12',
						description: '',
						id: draggedField._id,
						label: draggedField.fieldName,
						nestedSections: [],
						objectClass: draggedField._id.startsWith('_')
							? 'baseInput'
							: draggedField._id.startsWith('*') ? 'customInput' : 'mappedInput',
						attendeeField: draggedField._id.startsWith('_')
							? { propertyType: 'base', propertyName: draggedField._id.substring(1) }
							: draggedField._id.startsWith('*')
								? { propertyType: 'unattached' }
								: { propertyType: 'custom', customField: draggedField._id },
						options: draggedField.options || [],
						placeholder: draggedField.placeholder,
						required: draggedField.required,
						disabled: draggedField.disabled,
						type: draggedField.fieldType,
						_id: draggedField._id.startsWith('*')
							? `${draggedField._id}_${droppedInSectionIndex}_${sections[
								droppedInSectionIndex
							].fields.filter((ff) => ff._id.includes(draggedField._id)).length}`
							: draggedField._id,
						moduleConfiguration: draggedField.moduleConfiguration
					});
					sections[droppedInSectionIndex].fields = reorder(
						sections[droppedInSectionIndex].fields,
						nextIndex,
						result.destination.index
					);
				} else if (result.source.droppableId === 'drop-reg-buttons') {
					const nextIndex = sections[droppedInSectionIndex].buttons.length;
					sections[droppedInSectionIndex].buttons.push(result.source.index);
					sections[droppedInSectionIndex].buttons = reorder(
						sections[droppedInSectionIndex].buttons,
						nextIndex,
						result.destination.index
					);
				} else if (result.source.droppableId != result.destination.droppableId) {
					const removeFromSectionIndex = sections.findIndex((sec) => sec._id == result.source.droppableId);
					if (removeFromSectionIndex > -1) {
						const nextIndex = sections[droppedInSectionIndex].fields.length;
						const removedField = sections[removeFromSectionIndex].fields[result.source.index];
						sections[removeFromSectionIndex].fields.splice(result.source.index, 1);
						sections[droppedInSectionIndex].fields.push(removedField);
						sections[droppedInSectionIndex].fields = reorder(
							sections[droppedInSectionIndex].fields,
							nextIndex,
							result.destination.index
						);
					}
				}
				this.props.updateForm(updatedForm);
			}
		} else if (result.type === 'section') {
			const updatedForm = this.props.editedForm;
			const sections = updatedForm.pages[currentPage].sections;
			const reorderedSections = reorder(sections, result.source.index, result.destination.index);
			updatedForm.pages[currentPage].sections = reorderedSections;
			this.props.updateForm(updatedForm);
		}
		if (focusedFieldId) {
			this.setState({
				focusedFieldIndex: result.destination.index
			});
		}
	}

	closeFieldModal = () => {
		this.setState({
			adding: false,
			fieldSelected: false
		});
	};

	cancelEdit = () => {
		this.props.cancelEdit();
		this.setState({
			fieldSelected: false,
			focusOnField: false,
			focusedFieldId: null,
			focusedFieldIndex: null,
			focusedSectionId: null
		});
	};

	updateFormField = (sectionId, fieldId, propertyName, value) => {
		const updatedForm = this.props.editedForm;
		const sections = updatedForm.pages[this.state.currentPage].sections;
		const editedSectionIndex = sections.findIndex((s) => s._id == sectionId);
		if (editedSectionIndex > -1) {
			const updatedFieldIndex = sections[editedSectionIndex].fields.findIndex((f) => f._id == fieldId);
			if (updatedFieldIndex > -1) {
				const updatedField = sections[editedSectionIndex].fields[updatedFieldIndex];
				// If `id` is empty, set it to the `fieldId`
				if (!updatedField.id || updatedField.id.trim() === "") {
					updatedField.id = fieldId;
				}
				updatedField[propertyName] = value;
				sections[editedSectionIndex].fields[updatedFieldIndex] = updatedField;

				this.props.updateForm(updatedForm);
			}
		}
	};

	updateNestedFormField = (sectionId, fieldId, nestedSectionId, nestedFieldId, propertyName, value) => {
		const updatedForm = this.props.editedForm;
		const specialFields = ['salesForceSearchContact', 'salesForceSearchAccount'];
		const baseFieldList = this.props.baseFieldList;
		const sections = updatedForm.pages[this.state.currentPage].sections;
		const editedSectionIndex = sections.findIndex((s) => s._id == sectionId);
		if (editedSectionIndex > -1) {
			const updatedFieldIndex = sections[editedSectionIndex].fields.findIndex((f) => f._id == fieldId);
			if (updatedFieldIndex > -1) {
				const updatedField = sections[editedSectionIndex].fields[updatedFieldIndex];
				const nestedSectionIndex = updatedField.nestedSections.findIndex((ns) => ns._id == nestedSectionId);
				if (nestedSectionIndex > -1) {
					if (!nestedFieldId) {
						updatedField.nestedSections[nestedSectionIndex][propertyName] = value;
					} else {
						const nestedFieldIndex = updatedField.nestedSections[nestedSectionIndex].questions.findIndex(
							(nsq) => nsq._id == nestedFieldId
						);
						if (nestedFieldIndex > -1) {
							updatedField.nestedSections[nestedSectionIndex].questions[nestedFieldIndex][
								propertyName
							] = value;
							if (propertyName === 'type' && specialFields.includes(value)) {
								//find the value in the baseFieldList
								let baseField = baseFieldList.find((f) => f._id === 'special');
								let fieldType = baseField.fields.find((f) => f.fieldType === value);
								//assign the moduleConfiguration if it doesn't exist
								if (!updatedField.nestedSections[nestedSectionIndex].questions[nestedFieldIndex].moduleConfiguration) {
									updatedField.nestedSections[nestedSectionIndex].questions[nestedFieldIndex].moduleConfiguration = fieldType.moduleConfiguration;
								}
							}
						}
					}
				}

				//updatedField[propertyName] = value;
				sections[editedSectionIndex].fields[updatedFieldIndex] = updatedField;
				this.props.updateForm(updatedForm);
			}
		}
	};

	updateFormFieldConfig = (sectionId, fieldId, propertyName, value) => {
		const updatedForm = this.props.editedForm;
		const sections = updatedForm.pages[this.state.currentPage].sections;
		const editedSectionIndex = sections.findIndex((s) => s._id == sectionId);
		if (editedSectionIndex > -1) {
			const updatedFieldIndex = sections[editedSectionIndex].fields.findIndex((f) => f._id == fieldId);
			if (updatedFieldIndex > -1) {
				const updatedField = sections[editedSectionIndex].fields[updatedFieldIndex];
				if (!updatedField.moduleConfiguration) {
					updatedField.moduleConfiguration = {};
				}
				updatedField.moduleConfiguration[propertyName] = value;
				sections[editedSectionIndex].fields[updatedFieldIndex] = updatedField;
				this.props.updateForm(updatedForm);
			}
		}
	};

	updateFormFieldConfigField = (sectionId, fieldId, configFieldId, propertyName, value) => {
		const updatedForm = this.props.editedForm;
		const sections = updatedForm.pages[this.state.currentPage].sections;
		const editedSectionIndex = sections.findIndex((s) => s._id == sectionId);
		if (editedSectionIndex > -1) {
			const updatedFieldIndex = sections[editedSectionIndex].fields.findIndex((f) => f._id == fieldId);
			if (updatedFieldIndex > -1) {
				const updatedField = sections[editedSectionIndex].fields[updatedFieldIndex];
				const configFieldIndex = updatedField.moduleConfiguration.fields.findIndex(
					(cf) => cf._id == configFieldId
				);
				if (configFieldIndex > -1) {
					updatedField.moduleConfiguration.fields[configFieldIndex][propertyName] = value;
				}
				sections[editedSectionIndex].fields[updatedFieldIndex] = updatedField;
				this.props.updateForm(updatedForm);
			}
		}
	};

	updateFormButton = (sectionId, buttonId, propertyName, value) => {
		const updatedForm = this.props.editedForm;
		const sections = updatedForm.pages[this.state.currentPage].sections;
		const editedSectionIndex = sections.findIndex((s) => s._id == sectionId);
		if (editedSectionIndex > -1) {
			const updatedButtonIndex = sections[editedSectionIndex].buttons.findIndex((b) => b._id == buttonId);
			if (updatedButtonIndex > -1) {
				const updatedButton = sections[editedSectionIndex].buttons[updatedButtonIndex];
				updatedButton[propertyName] = value;
				sections[editedSectionIndex].buttons[updatedButtonIndex] = updatedButton;
				this.props.updateForm(updatedForm);
			}
		}
	};

	addNewSection = () => {
		const updatedForm = this.props.editedForm;
		const { currentPage } = this.state;
		console.log(currentPage);
		updatedForm.pages[currentPage].sections.push({
			_id: `^${currentPage}-${updatedForm.pages[currentPage].sections.length}`,
			title: '',
			fields: [],
			buttons: []
		});
		this.props.updateForm(updatedForm);
	};

	removeSection = (sectionId) => {
		const updatedForm = this.props.editedForm;
		const updatedSections = updatedForm.pages[this.state.currentPage].sections.filter((s) => s._id != sectionId);
		updatedForm.pages[this.state.currentPage].sections = updatedSections;
		this.props.updateForm(updatedForm);
	};

	moveSection = (direction, sectionId) => {
		const updatedForm = this.props.editedForm;
		const sections = updatedForm.pages[this.state.currentPage].sections;
		const sectionIndex = sections.findIndex((s) => s._id == sectionId);
		if (sectionIndex > -1) {
			const section = sections[sectionIndex];
			sections.splice(sectionIndex, 1);
			if (direction === 'sectionUp') {
				if (sectionIndex === 0 && this.state.currentPage > 0) {
					updatedForm.pages[this.state.currentPage - 1].sections.push(section);
				} else if (sectionIndex > 0) {
					sections.splice(sectionIndex - 1, 0, section);
				}
			} else if (direction === 'sectionDown') {
				if (sectionIndex === sections.length && this.state.currentPage < updatedForm.pages.length - 1) {
					updatedForm.pages[this.state.currentPage + 1].sections.unshift(section);
				} else if (sectionIndex < sections.length) {
					sections.splice(sectionIndex + 1, 0, section);
				}
			}
			updatedForm.pages[this.state.currentPage].sections = sections;
			this.props.updateForm(updatedForm);
		}
	};

	removeFormField = (sectionId, fieldId) => {
		const updatedForm = this.props.editedForm;
		const sections = updatedForm.pages[this.state.currentPage].sections;
		const editedSectionIndex = sections.findIndex((s) => s._id == sectionId);
		if (editedSectionIndex > -1) {
			const updatedFieldIndex = sections[editedSectionIndex].fields.findIndex((f) => f._id == fieldId);
			if (updatedFieldIndex > -1) {
				sections[editedSectionIndex].fields.splice(updatedFieldIndex, 1);
				this.props.updateForm(updatedForm);
				this.setState({
					fieldSelected: false,
					focusedFieldId: null,
					focusedFieldIndex: null,
					focusedSection: null
				});
			}
		}
	};

	removeFormButton = (sectionId, buttonId) => {
		const updatedForm = this.props.editedForm;
		const sections = updatedForm.pages[this.state.currentPage].sections;
		const editedSectionIndex = sections.findIndex((s) => s._id == sectionId);
		if (editedSectionIndex > -1) {
			const updatedButtonIndex = sections[editedSectionIndex].buttons.findIndex((b) => b._id == buttonId);
			if (updatedButtonIndex > -1) {
				sections[editedSectionIndex].buttons.splice(updatedButtonIndex, 1);
				this.props.updateForm(updatedForm);
			}
		}
	};

	editFormField = (property, value) => {
		const updatedForm = this.props.editedForm;
		updatedForm[property] = value;
		this.props.updateForm(updatedForm);
	};

	addField = (data, cb) => {
		const { focusedField, customFields } = this.state;
		const { editedForm } = this.props;
		let existingCategory = false;
		const updatedCustomFields = customFields.map((cf) => {
			if (cf._id == data._id) {
				existingCategory = true;
				cf.options = data.fields.map((f) => {
					const name = f.fieldName;
					return {
						_id: f._id,
						category: f.title,
						categoryId: f._id,
						value: name,
						label: name
					};
				});
			}
			return cf;
		});
		if (!existingCategory) {
			updatedCustomFields.push({
				_id: data._id,
				category: data.title,
				label: data.title,
				value: data._id,
				options: data.fields.map((f) => {
					const name = f.fieldName;
					return {
						_id: f._id,
						category: data.title,
						categoryId: data._id,
						value: name,
						label: name
					};
				})
			});

		}
		const updatedFormPages = editedForm.pages.map(page => {
			page.sections = page.sections.map((section) => {
				if (section._id == focusedField.section) {
					if (focusedField.nested) {
						const focusedFieldIndex = section.fields.findIndex((field) => {
							return field._id == focusedField.fieldId;
						});
						if (focusedFieldIndex > -1) {
							const focusedNestedSectionIndex = section.fields[focusedFieldIndex].nestedSections.findIndex(
								(ns) => ns._id == focusedField.nestedSectionId
							);
							if (focusedNestedSectionIndex > -1) {
								const focusedNestedFieldIndex = section.fields[focusedFieldIndex].nestedSections[
									focusedNestedSectionIndex
								].questions.findIndex((nsq) => nsq._id == focusedField.id);
								if (focusedNestedFieldIndex > -1) {
									section.fields[focusedFieldIndex].nestedSections[focusedNestedSectionIndex].questions[
										focusedNestedFieldIndex
									].id =
										data.fields[data.fields.length - 1]._id;
									section.fields[focusedFieldIndex].nestedSections[focusedNestedSectionIndex].questions[
										focusedNestedFieldIndex
									].attendeeField.propertyType =
										'custom';
									section.fields[focusedFieldIndex].nestedSections[focusedNestedSectionIndex].questions[
										focusedNestedFieldIndex
									].objectClass =
										'mappedInput';
								}
							}
						}
					} else {
						const focusedFieldIndex = section.fields.findIndex((field) => {
							return field._id == focusedField.id;
						});
						if (focusedFieldIndex > -1) {
							section.fields[focusedFieldIndex].id = data.fields[data.fields.length - 1]._id;
							section.fields[focusedFieldIndex].attendeeField.propertyType = 'custom';
							section.fields[focusedFieldIndex].objectClass = 'mappedInput';
						}
					}
				}
				return section;
			});
			return page;
		});
		const updatedForm = { ...editedForm, pages: updatedFormPages };
		this.props.updateForm(updatedForm);
		this.setState({
			customFields: updatedCustomFields
		}, () => {
			if (cb && typeof cb === 'function') {
				cb();
			}
		});

		this.props.addNewField && this.props.addNewField('attendees', data, cb);
	};

	renderNestedFieldConfig = (section, field, nestedSection, nestedField) => {
		let configElement;

		switch (nestedField.type) {
			case 'list':
				configElement = (
					<div>
						<div className="labelContainer">
							<div className="formFieldLabel">Options</div>
							<div
								className="se-blue c-pointer"
								onClick={() => {
									const updatedOptions = nestedField.options;
									updatedOptions.push('');
									this.updateNestedFormField(
										section._id,
										field._id,
										nestedSection._id,
										nestedField._id,
										'options',
										updatedOptions
									);
								}}
							>
								+ Add option
							</div>
						</div>
						<div className="formEditlistOptions">
							{nestedField.options.map((opt, oi) => {
								return (
									<div className="flex-ac mb-10">
										<InputField
											value={opt}
											classes="m-0"
											onChange={(e) => {
												const updatedOptions = nestedField.options;
												updatedOptions[oi] = e.target.value;
												this.updateNestedFormField(
													section._id,
													field._id,
													nestedSection._id,
													nestedField._id,
													'options',
													updatedOptions
												);
											}}
										/>
										<i
											className="las la-times ml-10 c-pointer"
											onClick={() => {
												const updatedOptions = nestedField.options.filter((op) => op !== opt);
												this.updateNestedFormField(
													section._id,
													field._id,
													nestedSection._id,
													nestedField._id,
													'options',
													updatedOptions
												);
											}}
										/>
									</div>
								);
							})}
						</div>
					</div>
				);
				break;
			case 'groupSelection':
				let groups = this.props.groups || [];
				let mappedGroups = groups.map((g) => {
					return { label: g.name, value: g._id };
				});

				configElement = (
					<div className="formFieldContainer">
						<div className="labelContainer">
							<div className="formFieldLabel">Groups</div>
							<div
								className="se-blue c-pointer "
								onClick={() => {
									const updatedOptions = nestedField.options;
									updatedOptions.push('');
									this.updateNestedFormField(
										section._id,
										field._id,
										nestedSection._id,
										nestedField._id,
										'options',
										updatedOptions
									);
								}}
							>
								+ Add group
							</div>
						</div>
						<div className="formEditlistOptions">
							{nestedField.options.map((opt, oi) => {
								return (
									<div className="flex-ac mb-10">
										<SelectField
											label=""
											classes="w-100"
											value={mappedGroups.find((g) => g.value == opt)}
											name="Groups"
											options={mappedGroups.filter(gr => !nestedField.options.includes(gr.value)).sort((a, b) => a.label.localeCompare(b.label))}
											required
											onChange={(e) => {
												const updatedOptions = nestedField.options;
												updatedOptions[oi] = e.value;
												this.updateNestedFormField(
													section._id,
													field._id,
													nestedSection._id,
													nestedField._id,
													'options',
													updatedOptions
												);
											}}
										/>
										<i
											className="las la-times ml-10 c-pointer"
											onClick={() => {
												const updatedOptions = nestedField.options.filter((op) => op !== opt);
												this.updateNestedFormField(
													section._id,
													field._id,
													nestedSection._id,
													nestedField._id,
													'options',
													updatedOptions
												);
											}}
										/>
									</div>
								);
							})}
						</div>

					</div>
				);
				break;
			case 'email':
				configElement = <div />;
				break;
			case 'medProLookup':
				const updatedModuleConfigurations = nestedField.moduleConfiguration ? nestedField.moduleConfiguration : {};

				configElement = <div >
					<ToggleField
						label="Capture Data"
						checked={updatedModuleConfigurations.captureData}
						icons={false}
						onChange={(e) => {
							this.updateNestedFormField(section._id, field._id, nestedSection._id,
								nestedField._id, 'moduleConfiguration', { ...updatedModuleConfigurations, captureData: e.target.checked })
						}}
					/></div>;
				break;
			case 'salesForceSearchContact':
				const updatedModuleConfigurationsSFC = nestedField.moduleConfiguration ? nestedField.moduleConfiguration : {};

				configElement = <div >
					<ToggleField
						label="Do not add to campaign"
						checked={updatedModuleConfigurationsSFC.doNotAddToCampaign}
						icons={false}
						onChange={(e) => {
							this.updateNestedFormField(section._id, field._id, nestedSection._id,
								nestedField._id, 'moduleConfiguration', { ...updatedModuleConfigurationsSFC, doNotAddToCampaign: e.target.checked })
						}}
					/></div>;
				break;
			case 'salesForceSearchAccount':
				/* const updatedModuleConfigurationsSFA = nestedField.moduleConfiguration ? nestedField.moduleConfiguration : {};

				configElement = <div >
					<ToggleField
						label="Capture Data"
						checked={updatedModuleConfigurationsSFA.captureData}
						icons={false}
						onChange={(e) => {
							this.updateNestedFormField(section._id, field._id, nestedSection._id,
								nestedField._id, 'moduleConfiguration', { ...updatedModuleConfigurationsSFA, captureData: e.target.checked })
						}}
					/></div>; */
				break;
			case 'phoneNumber':
				configElement = (
					<div className="formFieldContainer">
						<ToggleField
							label="Enable International Phone Numbers"
							checked={nestedField.moduleConfiguration?.enableInternational || false}
							icons={false}
							onChange={(e) => {
								const updatedConfig = nestedField.moduleConfiguration || {};
								updatedConfig.enableInternational = e.target.checked;
								this.updateNestedFormField(
									section._id,
									field._id,
									nestedSection._id,
									nestedField._id,
									'moduleConfiguration',
									updatedConfig
								);
							}}
						/>
						<InputField
							label="Default Country Code"
							value={nestedField.moduleConfiguration?.defaultCode || ''}
							placeholder="e.g., US"
							onChange={(e) => {
								const updatedConfig = nestedField.moduleConfiguration || {};
								updatedConfig.defaultCode = e.target.value;
								this.updateNestedFormField(
									section._id,
									field._id,
									nestedSection._id,
									nestedField._id,
									'moduleConfiguration',
									updatedConfig
								);
							}}
						/>
					</div>
				);
				break;
			case 'multipleCheck':
				configElement = (
					<div>
						<div className="labelContainer">
							<div className="formFieldLabel">Options</div>

							<div
								className="se-blue c-pointer "
								onClick={() => {
									const updatedOptions = nestedField.options;
									updatedOptions.push('');
									this.updateNestedFormField(
										section._id,
										field._id,
										nestedSection._id,
										nestedField._id,
										'options',
										updatedOptions
									);
								}}
							>
								+ Add option
							</div>
						</div>
						<div className="formEditlistOptions">
							{nestedField.options.map((opt, oi) => {
								return (
									<div className="flex-ac mb-10">
										<InputField
											value={opt}
											classes="m-0"
											onChange={(e) => {
												const updatedOptions = nestedField.options;
												updatedOptions[oi] = e.target.value;
												this.updateNestedFormField(
													section._id,
													field._id,
													nestedSection._id,
													nestedField._id,
													'options',
													updatedOptions
												);
											}}
										/>
										<i
											className="las la-times ml-10 c-pointer"
											onClick={() => {
												const updatedOptions = nestedField.options.filter((op) => op !== opt);
												this.updateNestedFormField(
													section._id,
													field._id,
													nestedSection._id,
													nestedField._id,
													'options',
													updatedOptions
												);
											}}
										/>
									</div>
								);
							})}
						</div>

						<ToggleField
							label="Enable 'Other' Option"
							checked={nestedField.enableOtherOption}
							icons={false}
							onChange={(e) => {
								const r = e.target.checked;
								this.updateNestedFormField(
									section._id,
									field._id,
									nestedSection._id,
									nestedField._id,
									'enableOtherOption',
									r
								);
							}}
						/>
					</div>
				);
				break;
			case 'select':
				configElement = (
					<div className="formEditlistOptions">
						<div className="labelContainer">
							<div className="formFieldLabel">Options</div>

							<div
								className="se-blue c-pointer "
								onClick={() => {
									const updatedOptions = nestedField.options;
									updatedOptions.push('');
									this.updateNestedFormField(
										section._id,
										field._id,
										nestedSection._id,
										nestedField._id,
										'options',
										updatedOptions
									);
								}}
							>
								+ Add option
							</div>
						</div>
						<div>
							{nestedField.options &&
								nestedField.options.map((opt, oi) => {
									return (
										<div className="flex-ac mb-10">
											<InputField
												value={opt}
												classes="m-0"
												onChange={(e) => {
													const updatedOptions = nestedField.options;
													updatedOptions[oi] = e.target.value;
													this.updateNestedFormField(
														section._id,
														field._id,
														nestedSection._id,
														nestedField._id,
														'options',
														updatedOptions
													);
													this.updateNestedFormField(
														section._id,
														field._id,
														nestedSection._id,
														nestedField._id,
														'defaultValue',
														''
													);
												}}
											/>
											<i
												className="las la-times ml-10 c-pointer"
												onClick={() => {
													const updatedOptions = nestedField.options.filter(
														(op) => op !== opt
													);
													this.updateNestedFormField(
														section._id,
														field._id,
														nestedSection._id,
														nestedField._id,
														'options',
														updatedOptions
													);
												}}
											/>
										</div>
									);
								})}
						</div>

						<ToggleField
							label="Enable 'Other' Option"
							checked={nestedField.enableOtherOption}
							icons={false}
							onChange={(e) => {
								const r = e.target.checked;
								this.updateNestedFormField(
									section._id,
									field._id,
									nestedSection._id,
									nestedField._id,
									'enableOtherOption',
									r
								);
							}}
						/>
					</div>
				);
				break;
			case 'rating':
				configElement = <div />;
				break;
			default:
				break;
		}
		return configElement ? (
			<div className="fieldTypeConfig mt-10">
				<label className="calibreSemiBold">Config</label>
				{configElement}
			</div>
		) : (
			''
		);
	};

	formatBaseFieldsForSelect = (baseFieldList) => {
		return baseFieldList.map((category) => ({
			label: category.title,
			value: category._id,
			_id: category._id,
			options: category.fields.map((f) => ({
				value: f._id.substring(1),
				label: f.fieldName,
				_id: f._id.substring(1),
				propertyType: 'base',
				fieldType: f.fieldType,
				fieldName: f.fieldName,
				originalId: f._id
			}))
		}));
	};

	renderFocusedFieldConfig = (section, field) => {
		let configElement;
		let isModuleField = false;
		if (field) {
			const attendeeFieldType = field && field.attendeeField ? field.attendeeField.propertyType : 'unattached';
			let currentCustomField = {};
			this.state.customFields.forEach((c) =>
				c.options.forEach((o) => {
					if (o._id == field.id) {
						currentCustomField = o;
					}
				})
			);

			switch (field.type) {
				case 'nestedSections':
					const formattedOptions = field.options
						? field.options.map((opt) => {
							return { label: opt, value: opt.replace(/ /g, '') };
						})
						: [];
					const baseFields = this.formatBaseFieldsForSelect(this.props.baseFieldList || []);
					configElement = (
						<div>
							<div className="formFieldContainer">
								<div className="labelContainer">
									<div className="formFieldLabel">Options</div>
									<div
										className="se-blue c-pointer "
										onClick={() => {
											const updatedOptions = field.options;
											updatedOptions.push('');
											this.updateFormField(section._id, field._id, 'options', updatedOptions);
										}}
									>
										+ Add option
									</div>
								</div>
								<div className="formEditlistOptions">
									{field.options.map((opt, oi) => {
										return (
											<div className="flex-ac mb-10">
												<InputField
													value={opt}
													classes="m-0"
													onChange={(e) => {
														const updatedOptions = field.options;
														updatedOptions[oi] = e.target.value;
														this.updateFormField(
															section._id,
															field._id,
															'options',
															updatedOptions
														);
													}}
												/>
												<i
													className="las la-times ml-10 c-pointer"
													onClick={() => {
														const updatedOptions = field.options.filter((op) => op !== opt);
														this.updateFormField(
															section._id,
															field._id,
															'options',
															updatedOptions
														);
													}}
												/>
											</div>
										);
									})}
								</div>
							</div>
							<div className="formFieldContainer">
								<div className="labelContainer mb-10">
									<div className="">Sections</div>
									<div
										className="se-blue c-pointer "
										onClick={() => {
											const updatedNestedSections = field.nestedSections || [];
											updatedNestedSections.push({
												_id: `_ns_${updatedNestedSections.length}`,
												trigger: '',
												sectionTitle: '',
												questions: []
											});
											this.updateFormField(
												section._id,
												field._id,
												'nestedSections',
												updatedNestedSections
											);
										}}
									>
										+ Add nested section
									</div>
								</div>
								<div className="nestedSectionFormEditor ">
									{field.nestedSections.map((ns, nsi) => (
										<div className="formFieldContainer" key={
											`nestedSection-${nsi}`}>
											<div className="labelContainer mb-10">
												<div className="">Section {nsi + 1}</div>

												<div className="flex-ac">
													{nsi > 0 && (
														<div
															className=" mr-10 c-pointer"
															onClick={() => {
																//move section up
																const updatedNestedSections = field.nestedSections;
																const temp = updatedNestedSections[nsi - 1];
																updatedNestedSections[nsi - 1] = updatedNestedSections[nsi];
																updatedNestedSections[nsi] = temp;
																this.updateFormField(
																	section._id,
																	field._id,
																	'nestedSections',
																	updatedNestedSections
																);
															}}
														>
															<i className="las la-caret-square-up" /> Move up
														</div>
													)}
													{nsi < field.nestedSections.length - 1 && (
														<div
															className=" mr-10 c-pointer"
															onClick={() => {
																//move section down
																const updatedNestedSections = field.nestedSections;
																const temp = updatedNestedSections[nsi + 1];
																updatedNestedSections[nsi + 1] = updatedNestedSections[nsi];
																updatedNestedSections[nsi] = temp;
																this.updateFormField(
																	section._id,
																	field._id,
																	'nestedSections',
																	updatedNestedSections
																);
															}}
														>
															<i className="las la-caret-square-down" /> Move Down
														</div>
													)}
													<div
														onClick={() => {
															//remove section
															const updatedNestedSections = field.nestedSections;
															updatedNestedSections.splice(nsi, 1);
															this.updateFormField(
																section._id,
																field._id,
																'nestedSections',
																updatedNestedSections
															);
														}}
														className="  c-pointer red"
													>
														<i className="las la-trash" /> Remove
													</div>
												</div>
											</div>
											<SelectField

												classes="w-100"
												label="Trigger"
												value={formattedOptions.find((t) => t.value == ns.trigger)}
												options={formattedOptions}
												onChange={(e) => {
													this.updateNestedFormField(
														section._id,
														field._id,
														ns._id,
														false,
														'trigger',
														e.value
													);
												}}
											/>
											<div className="labelContainer">
												<div className="formFieldLabel">Section Title</div>
											</div>
											<RichTextEditor
												id={`field-${field._id}-nestedSectionTitle${nsi}`}
												value={ns.sectionTitle}
												onChange={(e) =>
													this.updateNestedFormField(
														section._id,
														field._id,
														ns._id,
														false,
														'sectionTitle',
														e
													)}
												options={['image']}
												fontFamilies={[
													"'Museo Sans 300'",
													"'Museo Sans 500'",
													"'Museo Sans 700'",
													"'Proxima Nova Regular'",
													"'Proxima Nova Bold'"
												]}
												customFields={this.props.customFields}
											/>
											<div className="labelContainer mt-25 mb-10">
												<div className="">Section Questions</div>
												<div
													className="ml-a se-blue c-pointer "
													onClick={() => {
														const updatedQuestions = ns.questions || [];
														updatedQuestions.push({
															attendeeField: { propertyType: 'unattached' },
															columnClass: 'col-12',
															description: '',
															disabled: false,
															id: '',
															label: '',
															objectClass: 'customInput',
															options: [],
															placeholder: '',
															required: false,
															style: '',
															type: 'custom',
															_id: `*newquestion${ns.questions.length}`
														});
														this.updateNestedFormField(
															section._id,
															field._id,
															ns._id,
															false,
															'questions',
															updatedQuestions
														);
													}}
												>
													+ Add question
												</div>
											</div>
											<div className="w-100">
												{ns.questions.map((nsq, qi) => {
													const attendeeNSFieldType =
														ns && nsq.attendeeField
															? nsq.attendeeField.propertyType
															: 'unattached';
													let currentNSCustomField = {};
													const isHiddenFieldOption = (option = {}) => {
														const normalizedOptionId = ( option.value || '').toString().trim();
														return (
															hiddenAttendeeFieldTypes.includes(normalizedOptionId) 
														);
													};

													let nestedFields = [...baseFields, ...this.state.customFields]
														.map((group) => {
															if (!group.options) return group;
															return {
																...group,
																options: group.options.filter((option) => !isHiddenFieldOption(option))
															};
														})
													nestedFields.forEach((c) => {
														if (c.options) {
															c.options.forEach((o) => {
																if (o._id == nsq.id || (nsq.attendeeField && o._id == nsq.attendeeField.propertyName)) {
																	currentNSCustomField = o;
																}
															})
														} else {

															if (c._id.substring(1) == nsq.id || (nsq.attendeeField && c._id.substring(1) == nsq.attendeeField.propertyName)) {
																currentNSCustomField = c;
															}
														}
													}
													);
													return (
														<div className="w-100 mb-20 nestedSectionQuestionsFormEditor">
															<div className="labelContainer mb-10">
																<div className="">
																	Question {qi + 1}
																</div>
																<div className="flex-ac">
																	{qi > 0 && (
																		<div
																			className=" mr-10 c-pointer"
																			onClick={() => {
																				//move question up
																				const updatedQuestions = ns.questions;
																				const temp = updatedQuestions[qi - 1];
																				updatedQuestions[qi - 1] =
																					updatedQuestions[qi];
																				updatedQuestions[qi] = temp;
																				this.updateNestedFormField(
																					section._id,
																					field._id,
																					ns._id,
																					false,
																					'questions',
																					updatedQuestions
																				);
																			}}
																		>
																			<i className="las la-caret-square-up" /> Move up
																		</div>
																	)}
																	{qi < ns.questions.length - 1 && (
																		<div
																			className=" mr-10 c-pointer"
																			onClick={() => {
																				const updatedQuestions = ns.questions.slice(
																					0
																				);
																				updatedQuestions.splice(
																					qi,
																					0,
																					updatedQuestions.splice(qi + 1, 1)[0]
																				);
																				this.updateNestedFormField(
																					section._id,
																					field._id,
																					ns._id,
																					false,
																					'questions',
																					updatedQuestions
																				);
																			}}
																		>
																			<i className="las la-caret-square-down" /> Move
																			Down
																		</div>
																	)}
																	<div
																		onClick={() => {
																			const updatedQuestions = ns.questions.filter(
																				(qs) => qs._id !== nsq._id
																			);
																			this.updateNestedFormField(
																				section._id,
																				field._id,
																				ns._id,
																				false,
																				'questions',
																				updatedQuestions
																			);
																		}}
																		className="  c-pointer red"
																	>
																		<i className="las la-trash" /> Remove
																	</div>
																</div>
															</div>

															{!hiddenAttendeeFieldTypes.includes(nsq.type) && (
																<SelectField
																	label="Attendee Field"

																	classes="w-100"
																	value={currentNSCustomField}
																	options={nestedFields}
																	actionMessage={
																		<div
																			className="se-blue c-pointer"
																			onClick={() =>
																				this.setState({
																					addCustomFieldModalOpen: true,
																					focusedField: {
																						section: section._id,
																						nested: true,
																						id: nsq._id,
																						label: field.label
																					}
																				})}
																		>
																			Create custom field
																		</div>
																	}
																	actionOn={attendeeNSFieldType === 'unattached'}
																	formatGroupLabel={(data) => (
																		<div style={groupStyles}>
																			<span>{data.label}</span>
																			<span style={groupBadgeStyles}>
																				{data.options.length}
																			</span>
																		</div>
																	)}
																	onChange={(data) => {
																		let isBaseField = false;
																		let dataId = data._id;
																		let propertyType = data.propertyType || 'custom';
																		let objectClass = 'mappedInput';
																		if (data.propertyType === 'base') {
																			isBaseField = true;
																			objectClass = 'baseInput';
																		}

																		this.updateNestedFormField(
																			section._id,
																			field._id,
																			ns._id,
																			nsq._id,
																			'id',
																			dataId
																		);
																		this.updateNestedFormField(
																			section._id,
																			field._id,
																			ns._id,
																			nsq._id,
																			'attendeeField',
																			{
																				...nsq.attendeeField,
																				propertyName: dataId,
																				propertyType: propertyType
																			}
																		);
																		this.updateNestedFormField(
																			section._id,
																			field._id,
																			ns._id,
																			nsq._id,
																			'objectClass',
																			objectClass
																		);

																		if (isBaseField) {
																			this.updateNestedFormField(
																				section._id,
																				field._id,
																				ns._id,
																				nsq._id,
																				'label',
																				data.fieldName
																			);
																			this.updateNestedFormField(
																				section._id,
																				field._id,
																				ns._id,
																				nsq._id,
																				'type',
																				data.fieldType
																			);
																		}
																	}}
																/>
															)}
															<InputField
																label="Label"
																value={nsq.label}
																onChange={(e) => {
																	this.updateNestedFormField(
																		section._id,
																		field._id,
																		ns._id,
																		nsq._id,
																		'label',
																		e.target.value
																	);
																}}
															/>
															<div className="flex-ac">
																<SelectField
																	label="Field Type"

																	classes="w-100"
																	value={fieldTypes.find((t) => t.value == nsq.type)}
																	name="fieldType"
																	style={{ marginRight: 10 }}
																	options={fieldTypes}
																	required
																	onChange={(e) => {
																		this.updateNestedFormField(
																			section._id,
																			field._id,
																			ns._id,
																			nsq._id,
																			'type',
																			e.value
																		);
																	}}
																/>
																<SelectField
																	label="Column Size"

																	classes="w-100"
																	value={columnSizeOptions.find(
																		(cso) => cso.value == nsq.columnClass
																	)}
																	options={columnSizeOptions}
																	onChange={(e) => {
																		this.updateNestedFormField(
																			section._id,
																			field._id,
																			ns._id,
																			nsq._id,
																			'columnClass',
																			e.value
																		);
																	}}
																/>
															</div>

															<div className="flex-ac">
																{nsq.type === 'select' || nsq.type === 'nestedSections' ?
																	<SelectField
																		classes="mr-10"
																		label="Default Value"
																		value={nsq.defaultValue ? { value: nsq.defaultValue, label: nsq.defaultValue } : ''}
																		options={[{ label: '', value: '' }, ...nsq.options.map((o) => ({ label: o, value: o }))]}
																		onChange={(e) => this.updateNestedFormField(
																			section._id,
																			field._id,
																			ns._id,
																			nsq._id,
																			'defaultValue',
																			e.value
																		)}
																	/> :
																	<InputField
																		label="Default Value"
																		value={nsq.defaultValue}
																		type={nsq.type === 'date' ? 'date' : ''}
																		inputIcon={nsq.type === 'date' && <i className="lar la-calendar" />}
																		onChange={(e) => {
																			this.updateNestedFormField(
																				section._id,
																				field._id,
																				ns._id,
																				nsq._id,
																				'defaultValue',
																				e.target.value
																			);
																		}}
																		classes="mr-10"
																	/>}
																<InputField
																	label="PlaceHolder"
																	type={nsq.type === 'date' ? 'date' : ''}
																	inputIcon={nsq.type === 'date' && <i className="lar la-calendar" />}
																	value={nsq.placeholder}
																	onChange={(e) =>
																		this.updateNestedFormField(
																			section._id,
																			field._id,
																			ns._id,
																			nsq._id,
																			'placeholder',
																			e.target.value
																		)}
																/>
															</div>
															<ToggleField
																label="Required"
																checked={nsq.required}
																icons={false}
																onChange={(e) => {
																	const r = e.target.checked;
																	this.updateNestedFormField(
																		section._id,
																		field._id,
																		ns._id,
																		nsq._id,
																		'required',
																		r
																	);
																}}
															/>

															{nsq.type == 'inventoryLookup' && (
																<RichTextEditor
																	id={`field-${field._id}-nestedField-${nsq._id}-errorMessage`}
																	label="Inventory Lookup Error Message"
																	value={nsq?.moduleConfiguration?.errorMessage || ''}
																	customFields={this.props.customFields}
																	onChange={(e) => {
																		let updatedModuleConfiguration = nsq.moduleConfiguration || {};
																		updatedModuleConfiguration.errorMessage = e;
																		this.updateNestedFormField(
																			section._id,
																			field._id,
																			ns._id,
																			nsq._id,
																			'moduleConfiguration',
																			updatedModuleConfiguration
																		);
																	}}
																/>
															)}
															{nsq.type == 'number' && (
																<ToggleField
																	label="Allow Negative Values"
																	checked={nsq.moduleConfiguration && nsq.moduleConfiguration.allowNegative}
																	icons={false}
																	onChange={(e) => {
																		const r = e.target.checked;
																		const updatedModuleConfiguration = nsq.moduleConfiguration || {};
																		updatedModuleConfiguration.allowNegative = r;
																		this.updateNestedFormField(
																			section._id,
																			field._id,
																			ns._id,
																			nsq._id,
																			'moduleConfiguration',
																			updatedModuleConfiguration
																		);
																	}}
																/>
															)}
															{this.renderNestedFieldConfig(section, field, ns, nsq)}
														</div>
													);
												})}
											</div>
											<hr />
										</div>
									))}
								</div>
							</div>
						</div>
					);
					break;
				case 'email':
					configElement = <div />;
					break;
				case 'groupSelection':
					let groups = this.props.groups || [];
					let mappedGroups = groups.map((g) => {
						return { label: g.name, value: g._id };
					});
					const formattedOptionsGroupSelection = field.options
						? field.options.map((opt) => {
							let foundMappedGroup = mappedGroups.find(mg => mg.value === opt);
							let foundLabel = foundMappedGroup ? foundMappedGroup.label : '';

							return { label: foundLabel, value: opt.replace(/ /g, '') };
						})
						: [];


					configElement = (
						<div>
							<div className="formFieldContainer">
								<div className="labelContainer">
									<div className="formFieldLabel">Groups</div>
									<div
										className="se-blue c-pointer "
										onClick={() => {
											const updatedOptions = field.options;
											updatedOptions.push('');
											this.updateFormField(section._id, field._id, 'options', updatedOptions);
										}}
									>
										+ Add group
									</div>
								</div>
								<div className="formEditlistOptions">
									{field.options.map((opt, oi) => {
										return (
											<div className="flex-ac mb-10">
												<SelectField
													label=""
													classes="w-100"
													value={mappedGroups.find((g) => g.value == opt)}
													name="Groups"
													options={mappedGroups.filter(gr => !field.options.includes(gr.value)).sort((a, b) => a.label.localeCompare(b.label))}
													required
													onChange={(e) => {
														const updatedOptions = field.options;
														updatedOptions[oi] = e.value;
														this.updateFormField(section._id, field._id, 'options', updatedOptions);
													}}
												/>
												<i
													className="las la-times ml-10 c-pointer"
													onClick={() => {
														const updatedOptions = field.options.filter((op) => op !== opt);
														this.updateFormField(section._id, field._id, 'options', updatedOptions);
													}}
												/>
											</div>
										);
									})}
								</div>

							</div>
							<div className="formFieldContainer">
								<div className="labelContainer mb-10">
									<div className="">Sections</div>
									<div
										className="se-blue c-pointer "
										onClick={() => {
											const updatedNestedSections = field.nestedSections || [];
											updatedNestedSections.push({
												_id: `_ns_${updatedNestedSections.length}`,
												trigger: '',
												sectionTitle: '',
												questions: []
											});
											this.updateFormField(
												section._id,
												field._id,
												'nestedSections',
												updatedNestedSections
											);
										}}
									>
										+ Add nested section
									</div>
								</div>
								<div className="nestedSectionFormEditor ">
									{field.nestedSections.map((ns, nsi) => (
										<div className="formFieldContainer" key={
											`nestedSection-${nsi}`}>
											<div className="labelContainer mb-10">
												<div className="">Section {nsi + 1}</div>

												<div className="flex-ac">
													{nsi > 0 && (
														<div
															className=" mr-10 c-pointer"
															onClick={() => {
																//move section up
																const updatedNestedSections = field.nestedSections;
																const temp = updatedNestedSections[nsi - 1];
																updatedNestedSections[nsi - 1] = updatedNestedSections[nsi];
																updatedNestedSections[nsi] = temp;
																this.updateFormField(
																	section._id,
																	field._id,
																	'nestedSections',
																	updatedNestedSections
																);
															}}
														>
															<i className="las la-caret-square-up" /> Move up
														</div>
													)}
													{nsi < field.nestedSections.length - 1 && (
														<div
															className=" mr-10 c-pointer"
															onClick={() => {
																//move section down
																const updatedNestedSections = field.nestedSections;
																const temp = updatedNestedSections[nsi + 1];
																updatedNestedSections[nsi + 1] = updatedNestedSections[nsi];
																updatedNestedSections[nsi] = temp;
																this.updateFormField(
																	section._id,
																	field._id,
																	'nestedSections',
																	updatedNestedSections
																);
															}}
														>
															<i className="las la-caret-square-down" /> Move Down
														</div>
													)}
													<div
														onClick={() => {
															//remove section
															const updatedNestedSections = field.nestedSections;
															updatedNestedSections.splice(nsi, 1);
															this.updateFormField(
																section._id,
																field._id,
																'nestedSections',
																updatedNestedSections
															);
														}}
														className="  c-pointer red"
													>
														<i className="las la-trash" /> Remove
													</div>
												</div>
											</div>
											<SelectField

												classes="w-100"
												label="Trigger"
												value={formattedOptionsGroupSelection.find((t) => t.value == ns.trigger)}
												options={formattedOptionsGroupSelection}
												onChange={(e) => {
													this.updateNestedFormField(
														section._id,
														field._id,
														ns._id,
														false,
														'trigger',
														e.value
													);
												}}
											/>
											<div className="labelContainer">
												<div className="formFieldLabel">Section Title</div>
											</div>
											<RichTextEditor
												id={`field-${field._id}-nestedSectionTitle${nsi}`}
												value={ns.sectionTitle}
												onChange={(e) =>
													this.updateNestedFormField(
														section._id,
														field._id,
														ns._id,
														false,
														'sectionTitle',
														e
													)}
												options={['image']}
												fontFamilies={[
													"'Museo Sans 300'",
													"'Museo Sans 500'",
													"'Museo Sans 700'",
													"'Proxima Nova Regular'",
													"'Proxima Nova Bold'"
												]}
												customFields={this.props.customFields}
											/>
											<div className="labelContainer mt-25 mb-10">
												<div className="">Section Questions</div>
												<div
													className="ml-a se-blue c-pointer "
													onClick={() => {
														const updatedQuestions = ns.questions || [];
														updatedQuestions.push({
															attendeeField: { propertyType: 'unattached' },
															columnClass: 'col-12',
															description: '',
															disabled: false,
															id: '',
															label: '',
															objectClass: 'customInput',
															options: [],
															placeholder: '',
															required: false,
															style: '',
															type: 'custom',
															_id: `*newquestion${ns.questions.length}`
														});
														this.updateNestedFormField(
															section._id,
															field._id,
															ns._id,
															false,
															'questions',
															updatedQuestions
														);
													}}
												>
													+ Add question
												</div>
											</div>
											<div className="w-100">
												{ns.questions.map((nsq, qi) => {
													const attendeeNSFieldType =
														ns && nsq.attendeeField
															? nsq.attendeeField.propertyType
															: 'unattached';
													let currentNSCustomField = {};
													this.state.customFields.forEach((c) =>
														c.options.forEach((o) => {
															if (o._id == nsq.id || (nsq.attendeeField && o._id == nsq.attendeeField.propertyName)) {
																currentNSCustomField = o;
															}
														})
													);
													return (
														<div className="w-100 mb-20 nestedSectionQuestionsFormEditor">
															<div className="labelContainer mb-10">
																<div className="">
																	Question {qi + 1}
																</div>
																<div className="flex-ac">
																	{qi > 0 && (
																		<div
																			className=" mr-10 c-pointer"
																			onClick={() => {
																				//move question up
																				const updatedQuestions = ns.questions;
																				const temp = updatedQuestions[qi - 1];
																				updatedQuestions[qi - 1] =
																					updatedQuestions[qi];
																				updatedQuestions[qi] = temp;
																				this.updateNestedFormField(
																					section._id,
																					field._id,
																					ns._id,
																					false,
																					'questions',
																					updatedQuestions
																				);
																			}}
																		>
																			<i className="las la-caret-square-up" /> Move up
																		</div>
																	)}
																	{qi < ns.questions.length - 1 && (
																		<div
																			className=" mr-10 c-pointer"
																			onClick={() => {
																				const updatedQuestions = ns.questions.slice(
																					0
																				);
																				updatedQuestions.splice(
																					qi,
																					0,
																					updatedQuestions.splice(qi + 1, 1)[0]
																				);
																				this.updateNestedFormField(
																					section._id,
																					field._id,
																					ns._id,
																					false,
																					'questions',
																					updatedQuestions
																				);
																			}}
																		>
																			<i className="las la-caret-square-down" /> Move
																			Down
																		</div>
																	)}
																	<div
																		onClick={() => {
																			const updatedQuestions = ns.questions.filter(
																				(qs) => qs._id !== nsq._id
																			);
																			this.updateNestedFormField(
																				section._id,
																				field._id,
																				ns._id,
																				false,
																				'questions',
																				updatedQuestions
																			);
																		}}
																		className="  c-pointer red"
																	>
																		<i className="las la-trash" /> Remove
																	</div>
																</div>
															</div>

															<SelectField
																label="Attendee Field"

																classes="w-100"
																value={currentNSCustomField}
																options={this.state.customFields}
																actionMessage={
																	<div
																		className="se-blue c-pointer"
																		onClick={() =>
																			this.setState({
																				addCustomFieldModalOpen: true,
																				focusedField: {
																					section: section._id,
																					nested: true,
																					id: nsq._id,
																					label: field.label
																				}
																			})}
																	>
																		Create custom field
																	</div>
																}
																actionOn={attendeeNSFieldType === 'unattached'}
																formatGroupLabel={(data) => (
																	<div style={groupStyles}>
																		<span>{data.label}</span>
																		<span style={groupBadgeStyles}>
																			{data.options.length}
																		</span>
																	</div>
																)}
																onChange={(data) => {
																	this.updateNestedFormField(
																		section._id,
																		field._id,
																		ns._id,
																		nsq._id,
																		'id',
																		data._id
																	);
																	this.updateNestedFormField(
																		section._id,
																		field._id,
																		ns._id,
																		nsq._id,
																		'attendeeField',
																		{
																			...nsq.attendeeField,
																			propertyName: data._id,
																			propertyType: 'custom'
																		}
																	);
																	this.updateNestedFormField(
																		section._id,
																		field._id,
																		ns._id,
																		nsq._id,
																		'objectClass',
																		'mappedInput'
																	);
																}}
															/>
															<InputField
																label="Label"
																value={nsq.label}
																onChange={(e) => {
																	this.updateNestedFormField(
																		section._id,
																		field._id,
																		ns._id,
																		nsq._id,
																		'label',
																		e.target.value
																	);
																}}
															/>
															<div className="flex-ac">
																<SelectField
																	label="Field Type"

																	classes="w-100"
																	value={fieldTypes.find((t) => t.value == nsq.type)}
																	name="fieldType"
																	style={{ marginRight: 10 }}
																	options={fieldTypes}
																	required
																	onChange={(e) => {
																		this.updateNestedFormField(
																			section._id,
																			field._id,
																			ns._id,
																			nsq._id,
																			'type',
																			e.value
																		);
																	}}
																/>
																<SelectField
																	label="Column Size"

																	classes="w-100"
																	value={columnSizeOptions.find(
																		(cso) => cso.value == nsq.columnClass
																	)}
																	options={columnSizeOptions}
																	onChange={(e) => {
																		this.updateNestedFormField(
																			section._id,
																			field._id,
																			ns._id,
																			nsq._id,
																			'columnClass',
																			e.value
																		);
																	}}
																/>
															</div>

															<div className="flex-ac">
																{nsq.type === 'select' || nsq.type === 'nestedSections' ?
																	<SelectField
																		classes="mr-10"
																		label="Default Value"
																		value={nsq.defaultValue ? { value: nsq.defaultValue, label: nsq.defaultValue } : ''}
																		options={[{ label: '', value: '' }, ...nsq.options.map((o) => ({ label: o, value: o }))]}
																		onChange={(e) => this.updateNestedFormField(
																			section._id,
																			field._id,
																			ns._id,
																			nsq._id,
																			'defaultValue',
																			e.value
																		)}
																	/> :
																	<InputField
																		label="Default Value"
																		value={nsq.defaultValue}
																		type={nsq.type === 'date' ? 'date' : ''}
																		inputIcon={nsq.type === 'date' && <i className="lar la-calendar" />}
																		onChange={(e) => {
																			this.updateNestedFormField(
																				section._id,
																				field._id,
																				ns._id,
																				nsq._id,
																				'defaultValue',
																				e.target.value
																			);
																		}}
																		classes="mr-10"
																	/>}
																<InputField
																	label="PlaceHolder"
																	type={nsq.type === 'date' ? 'date' : ''}
																	inputIcon={nsq.type === 'date' && <i className="lar la-calendar" />}
																	value={nsq.placeholder}
																	onChange={(e) =>
																		this.updateNestedFormField(
																			section._id,
																			field._id,
																			ns._id,
																			nsq._id,
																			'placeholder',
																			e.target.value
																		)}
																/>
															</div>
															<ToggleField
																label="Required"
																checked={nsq.required}
																icons={false}
																onChange={(e) => {
																	const r = e.target.checked;
																	this.updateNestedFormField(
																		section._id,
																		field._id,
																		ns._id,
																		nsq._id,
																		'required',
																		r
																	);
																}}
															/>
															{this.renderNestedFieldConfig(section, field, ns, nsq)}
														</div>
													);
												})}
											</div>
											<hr />
										</div>
									))}
								</div>
							</div>
						</div>
					);
					break;
				case 'redirectToPage':
					let pages = this.props.allPages || [];
					pages = pages.filter((p) => p.published && !p.isSessionPage).map((p) => {
						return { label: p.title, value: p._id };
					});

					configElement = (
						<div className="formFieldContainer">
							<div className="labelContainer">
								<div className="formFieldLabel">Options</div>
								<div
									className="se-blue c-pointer "
									onClick={() => {
										const updatedOptions = field.options;
										updatedOptions.push('');
										this.updateFormField(section._id, field._id, 'options', updatedOptions);
									}}
								>
									+ Add Page
								</div>
							</div>
							<div className="formEditlistOptions">
								{field.options.map((opt, oi) => {
									return (
										<div className="flex-ac mb-10">
											<SelectField
												classes="w-100"
												value={pages.find((t) => t.value == opt)}
												name="Pages"
												options={pages}
												onChange={(e) => {
													const updatedOptions = field.options;
													updatedOptions[oi] = e.value;
													this.updateFormField(section._id, field._id, 'options', updatedOptions);
												}}
											/>
											<i
												className="las la-times ml-10 c-pointer"
												onClick={() => {
													const updatedOptions = field.options.filter((op) => op !== opt);
													this.updateFormField(section._id, field._id, 'options', updatedOptions);
												}}
											/>

										</div>
									);
								})}
							</div>
						</div>
					);
					break;
				case 'select':
				case 'multipleCheck':
				case 'list':
					configElement = (
						<div className="formFieldContainer">
							<div className="labelContainer">
								<div className="formFieldLabel">Options</div>
								<div className="flex-ac">
									<div
										className="se-blue c-pointer mr-10"
										onClick={() => {
											const updatedOptions = field.options;
											updatedOptions.push('');
											this.updateFormField(section._id, field._id, 'options', updatedOptions);
										}}
									>
										+ Add option
									</div>
									<div
										className="se-blue c-pointer"
										onClick={() => {
											this.setState({
												uploadOptionsModalOpen: true,
												uploadTargetSection: section,
												uploadTargetField: field
											});
										}}
									>
										<i className="las la-file-upload mr-5" />
										Upload CSV
									</div>
								</div>
							</div>
							<div className="formEditlistOptions">
								{field.options.map((opt, oi) => {
									return (
										<div className="flex-ac mb-10">
											<InputField
												value={opt}
												classes="m-0"
												onChange={(e) => {
													const updatedOptions = field.options;
													updatedOptions[oi] = e.target.value;
													this.updateFormField(section._id, field._id, 'options', updatedOptions);
													this.updateFormField(
														section._id,
														field._id,
														'defaultValue',
														''
													);
												}}
											/>
											<i
												className="las la-times ml-10 c-pointer"
												onClick={() => {
													const updatedOptions = field.options.filter((op) => op !== opt);
													this.updateFormField(section._id, field._id, 'options', updatedOptions);
													this.updateFormField(
														section._id,
														field._id,
														'defaultValue',
														''
													);
												}}
											/>
										</div>
									);
								})}
							</div>
							<ToggleField
								label="Enable 'Other' Option"
								checked={field.enableOtherOption}
								icons={false}
								onChange={(e) => {
									const r = e.target.checked;
									this.updateFormField(section._id, field._id, 'enableOtherOption', r);
								}}
							/>
						</div>
					);
					break;
				case 'radio':
					configElement = (
						<div className="formFieldContainer">
							<div className="labelContainer">
								<div className="formFieldLabel">Options</div>
								<div
									className="se-blue c-pointer "
									onClick={() => {
										const updatedOptions = field.options ? field.options : [];
										updatedOptions.push('');
										this.updateFormField(section._id, field._id, 'options', updatedOptions);
									}}
								>
									+ Add option
								</div>
							</div>
							<div className="formEditlistOptions">
								{field.options && field.options.map((option, ri) => {
									return (
										<div className="flex-ac mb-10">
											<InputField
												label="Label"
												value={option}
												onChange={(e) => {
													const updatedOptions = field.options;
													updatedOptions[ri] = e.target.value;
													this.updateFormField(section._id, field._id, 'options', updatedOptions);
												}}
											/>

											<i
												className="las la-times ml-10 c-pointer"
												onClick={() => {
													const updatedOptions = field.options.filter((r, rfi) => ri !== rfi);
													this.updateFormField(section._id, field._id, 'options', updatedOptions);
												}}
											/>
										</div>
									);
								})}
							</div>
						</div>
					);
					break;
				case 'rating':
					configElement = (
						<div className="formFieldContainer">
							<div className="labelContainer">
								<div className="formFieldLabel">Ratings</div>
								<div
									className="se-blue c-pointer "
									onClick={() => {
										const updatedRatings = field.ratingScale ? field.ratingScale : [];
										updatedRatings.push({ label: '', value: '' });
										this.updateFormField(section._id, field._id, 'ratingScale', updatedRatings);
									}}
								>
									+ Add option
								</div>
							</div>
							<div className="formEditlistOptions">
								{field.ratingScale && field.ratingScale.map((rating, ri) => {
									return (
										<div className="flex-ac mb-10">
											<InputField
												label="Label"
												value={rating.label}
												onChange={(e) => {
													const updatedRatings = field.ratingScale;
													updatedRatings[ri].label = e.target.value;
													this.updateFormField(section._id, field._id, 'ratingScale', updatedRatings);
												}}
											/>
											<InputField
												value={rating.value}
												label="Value"
												classes="ml-10"
												onChange={(e) => {
													const updatedRatings = field.ratingScale;
													updatedRatings[ri].value = e.target.value;
													this.updateFormField(section._id, field._id, 'ratingScale', updatedRatings);
												}}
											/>
											<i
												className="las la-times ml-10 c-pointer"
												onClick={() => {
													const updatedRatings = field.ratingScale.filter((r, rfi) => ri !== rfi);
													this.updateFormField(section._id, field._id, 'ratingScale', updatedRatings);
												}}
											/>
										</div>
									);
								})}
							</div>
						</div>
					);
					break;
				case 'inventory':
					const inventoryOptions = this.props.program.eventDefaults.inventory.map((opt) => {
						return {
							label: opt.name,
							value: opt._id
						};
					});
					configElement = (
						<div className="formEditlistOptions">
							<SelectField
								label="Inventory Item"

								classes="w-100"
								value={inventoryOptions.find((t) => t.value == field.id)}
								name="Inventory Item"
								options={inventoryOptions}
								required
								onChange={(e) => {
									this.updateFormField(section._id, field._id, 'id', e.value);
								}}
							/>
						</div>
					);
					break;
				case 'singleInventoryRedemption':
					const invOptions = this?.props?.inventory ? this.props.inventory.map((opt) => {
						return {
							label: opt.name,
							value: opt._id
						};
					}) : [];
					configElement = (
						<div className="formEditlistOptions">
							<SelectField
								label="Inventory Item"

								classes="w-100"
								value={invOptions.find((t) => t.value == field.id)}
								name="Inventory Item"
								options={invOptions}
								required
								onChange={(e) => {
									this.updateFormField(section._id, field._id, 'id', e.value);
								}}
							/>
							<InputField
								label="Max Redemption Amount"
								type="number"
								value={field.moduleConfiguration?.maxRedemptionAmount || ''}
								placeholder="e.g., 5"
								onChange={(e) => {
									const updatedConfig = field.moduleConfiguration || {};
									updatedConfig.maxRedemptionAmount = e.target.value ? parseInt(e.target.value) : '';
									this.updateFormField(section._id, field._id, 'moduleConfiguration', updatedConfig);
								}}
							/>
						</div>
					);
					break;

				case 'additionalAttendees':
					isModuleField = true;
					const statusOptions = [
						{ label: 'Registered', value: 'registered' },
						{
							label: 'Invited',
							value: 'invited'
						}
					];
					const attendeeFields = field.moduleConfiguration.fields || [];
					configElement = (
						<div className="formFieldContainer">
							<div className=" w-100 flex-sb flex-ac mt-10">
								<h4>{field.label}</h4>
							</div>
							<InputField
								label="Attendee Label"
								value={field.moduleConfiguration.label}
								onChange={(e) =>
									this.updateFormFieldConfig(section._id, field._id, 'label', e.target.value)}
							/>
							<InputField
								type="number"
								label="Max Additional Attendees"
								value={field.moduleConfiguration.maxAttendees}
								onChange={(e) =>
									this.updateFormFieldConfig(section._id, field._id, 'maxAttendees', e.target.value)}
							/>
							{this.props.groups && this.props.groups.length > 0 && <SelectField label="Associated Group"
								classes="w-100" />}
							<SelectField
								label="Default Status"

								classes="w-100"
								value={statusOptions.find((f) => f.value == field.moduleConfiguration.defaultStatus)}
								options={statusOptions}
								onChange={(e) =>
									this.updateFormFieldConfig(section._id, field._id, 'defaultStatus', e.value)}
							/>
							{this.props.emails &&
								this.props.emails.length > 0 && (
									<SelectField
										label="Confirmation Email"

										classes="w-100"
										options={this.props.emails}
										value={this.props.emails.find(
											(e) => e.value == field.moduleConfiguration.confirmationEmail
										)}
										onChange={(e) =>
											this.updateFormFieldConfig(section._id, field._id, 'confirmationEmail', e.value)}
									/>
								)}
							<div className="labelContainer mt-25 mb-10">
								<div className="">Attendee Fields</div>
								<div
									className="ml-a se-blue c-pointer "
									onClick={() => {
										const updatedQuestions = attendeeFields || [];
										updatedQuestions.push({
											attendeeField: { propertyType: 'unattached' },
											columnClass: 'col-12',
											description: '',
											disabled: false,
											id: `*newquestion${attendeeFields.length}`,
											label: '',
											objectClass: 'xcustomInput',
											options: [],
											placeholder: '',
											required: false,
											style: '',
											type: 'custom',
											_id: `*newquestion${attendeeFields.length}`
										});
										this.updateFormFieldConfig(section._id, field._id, 'fields', updatedQuestions);
									}}
								>
									+ Add Field
								</div>
							</div>
							<div className="w-100">
								{attendeeFields.map((nsq, qi) => {
									const attendeeNSFieldType = nsq.attendeeField
										? nsq.attendeeField.propertyType
										: 'unattached';
									let currentNSCustomField = {};
									this.state.customFields.forEach((c) =>
										c.options.forEach((o) => {
											if (o._id == nsq.id) {
												currentNSCustomField = o;
											}
										})
									);
									let isBaseField = false;
									isBaseField = nsq.objectClass === 'xbaseInput';
									return (
										<div className="w-100 mb-20 nestedSectionQuestionsFormEditor">
											<div className="labelContainer mb-10">
												<div className="">Question {qi + 1}</div>
												{!nsq.static && (
													<div className="flex-ac">
														<div
															onClick={() => {
																const updatedQuestions = attendeeFields.filter(
																	(qs) => qs._id !== nsq._id
																);
																this.updateFormFieldConfig(
																	section._id,
																	field._id,
																	'fields',
																	updatedQuestions
																);
															}}
															className="  c-pointer red"
														>
															<i className="las la-trash" /> Remove
														</div>
													</div>
												)}
											</div>

											{nsq.attendeeField.propertyType !== 'base' && (
												<SelectField
													label="Attendee Field"

													classes="w-100"
													value={currentNSCustomField}
													options={this.state.customFields}
													actionMessage={
														<div
															className="se-blue c-pointer"
															onClick={() =>
																this.setState({
																	addCustomFieldModalOpen: true,
																	focusedField: {
																		section: section._id,
																		nested: true,
																		id: nsq._id,
																		label: field.label
																	}
																})}
														>
															Create custom field
														</div>
													}
													actionOn={attendeeNSFieldType === 'unattached'}
													formatGroupLabel={(data) => (
														<div style={groupStyles}>
															<span>{data.label}</span>
															<span style={groupBadgeStyles}>{data.options.length}</span>
														</div>
													)}
													onChange={(data) => {
														this.updateFormFieldConfigField(
															section._id,
															field._id,
															nsq._id,
															'customField',
															data._id
														);
														this.updateFormFieldConfigField(
															section._id,
															field._id,
															nsq._id,
															'attendeeField',
															{
																...field.attendeeField,
																propertyName: data._id,
																propertyType: 'custom'
															}
														);
														this.updateFormFieldConfigField(
															section._id,
															field._id,
															nsq._id,
															'objectClass',
															'mappedInput'
														);
													}}
												/>
											)}
											<InputField
												label="Label"
												value={nsq.label}
												onChange={(e) => {
													this.updateFormFieldConfigField(
														section._id,
														field._id,
														nsq._id,
														'label',
														e.target.value
													);
												}}
											/>
											<div className="flex-ac">
												{<SelectField
													label="Field Type"
													classes="w-100"
													value={fieldTypes.find((t) => t.value == nsq.type)}
													name="fieldType"
													style={{ marginRight: 10 }}
													options={fieldTypes}
													required
													onChange={(e) => {
														this.updateFormFieldConfigField(
															section._id,
															field._id,
															nsq._id,
															'type',
															e.value
														);
													}}
												/>}
												<SelectField
													label="Column Size"

													classes="w-100"
													value={columnSizeOptions.find((cso) => cso.value == nsq.columnClass)}
													options={columnSizeOptions}
													onChange={(e) => {
														this.updateFormFieldConfigField(
															section._id,
															field._id,
															nsq._id,
															'columnClass',
															e.value
														);
													}}
												/>
											</div>

											<div className="flex-ac">
												<InputField
													label="Default Value"
													value={nsq.defaultValue}
													onChange={(e) => {
														this.updateFormFieldConfigField(
															section._id,
															field._id,
															nsq._id,
															'defaultValue',
															e.target.value
														);
													}}
													classes="mr-10"
												/>
												<InputField
													label="PlaceHolder"
													value={nsq.placeholder}
													type={nsq.type === 'date' ? 'date' : ''}
													inputIcon={nsq.type === 'date' && <i className="lar la-calendar" />}
													onChange={(e) =>
														this.updateFormFieldConfigField(
															section._id,
															field._id,
															nsq._id,
															'placeholder',
															e.target.value
														)}
												/>
											</div>
											<ToggleField
												label="Required"
												checked={nsq.required}
												type={nsq.type === 'date' ? 'date' : ''}
												inputIcon={nsq.type === 'date' && <i className="lar la-calendar" />}
												icons={false}
												onChange={(e) => {
													const r = e.target.checked;
													this.updateFormFieldConfigField(
														section._id,
														field._id,
														nsq._id,
														'required',
														r
													);
												}}
											/>
											{/* this.renderNestedFieldConfig(section, field, ns, nsq) */}
											{this.renderFormFieldConfig(section, field, nsq)}
										</div>
									);
								})}
							</div>
						</div>
					);
					break;
				case 'calendarBooking':
					isModuleField = true;
					break;
				case 'notifierEmail':
					const notificationEmails = this.props.emails ? this.props.emails : [];
					const moduleConfiguration = field.moduleConfiguration || {};
					configElement = (<div>
						<SelectField
							label="Registration Confirmed Notification Email"

							classes="w-100"
							options={notificationEmails}
							value={notificationEmails.find(
								(e) => e.value == moduleConfiguration.notificationEmail
							)}
							onChange={(e) =>
								this.updateFormFieldConfig(section._id, field._id, 'notificationEmail', e.value)}
						/>
						<ToggleField
							label="Send Declined Notification Email"
							checked={moduleConfiguration.sendNotAttendingNotification}
							icons={false}
							onChange={(e) =>
								this.updateFormFieldConfig(section._id, field._id, 'sendNotAttendingNotification', e.target.checked)}
						/>

						{moduleConfiguration.sendNotAttendingNotification && <SelectField
							label="Registration Declined Notification Email"

							classes="w-100"
							options={notificationEmails}
							value={notificationEmails.find(
								(e) => e.value == moduleConfiguration.notAttendingNotificationEmail
							)}
							onChange={(e) =>
								this.updateFormFieldConfig(section._id, field._id, 'notAttendingNotificationEmail', e.value)}
						/>}

					</div>);
					break;
				case 'medProLookup':
					isModuleField = true;

					configElement = (
						<div className="formFieldContainer">
							<div className=" w-100 flex-sb flex-ac mt-10">
								<InputField
									label="Label"
									value={field.label}
									type="text"
									onChange={(e) => this.updateFormField(section._id, field._id, 'label', e.target.value)}
								/>
							</div>

							<ToggleField
								label="Capture Data"
								checked={field.moduleConfiguration.captureData}
								icons={false}
								onChange={(e) =>
									this.updateFormFieldConfig(section._id, field._id, 'captureData', e.target.checked)}
							/>
						</div>
					);
					break;
				case 'salesForceSearchContact':
					isModuleField = true;

					configElement = (
						<div className="formFieldContainer">
							<div className=" w-100 flex-sb flex-ac mt-10">
								<InputField
									label="Label"
									value={field.label}
									type="text"
									onChange={(e) => this.updateFormField(section._id, field._id, 'label', e.target.value)}
								/>
							</div>

							<ToggleField
								label="Do not add to campaign"
								checked={field.moduleConfiguration.doNotAddToCampaign}
								icons={false}
								onChange={(e) =>
									this.updateFormFieldConfig(section._id, field._id, 'doNotAddToCampaign', e.target.checked)}
							/>
						</div>
					);
					break;
				case 'salesForceSearchAccount':
					isModuleField = true;

					configElement = (
						<div className="formFieldContainer">
							<div className=" w-100 flex-sb flex-ac mt-10">
								<InputField
									label="Label"
									value={field.label}
									type="text"
									onChange={(e) => this.updateFormField(section._id, field._id, 'label', e.target.value)}
								/>
							</div>
						</div>
					);
					break;
				case 'addressLookup':
					const relatedFields = field.moduleConfiguration?.relatedFields || {
						address: '',
						address2: '',
						city: '',
						state: '',
						zip: '',
						country: ''
					};

					configElement = (
						<div className="formFieldContainer">
							<div className=" w-100 flex-sb flex-ac mt-10">
								<h4>Address Lookup Configuration</h4>
							</div>
							<p className="mb-20 small gray">Map the address lookup results to form fields</p>

							<InputField
								label="Address Field ID"
								value={relatedFields.address}
								placeholder="e.g., address_field_id"
								onChange={(e) => {
									const updatedConfig = field.moduleConfiguration || {};
									updatedConfig.relatedFields = {
										...relatedFields,
										address: e.target.value
									};
									this.updateFormFieldConfig(section._id, field._id, 'relatedFields', updatedConfig.relatedFields);
								}}
							/>

							<InputField
								label="Address 2 Field ID"
								value={relatedFields.address2}
								placeholder="e.g., address2_field_id"
								onChange={(e) => {
									const updatedConfig = field.moduleConfiguration || {};
									updatedConfig.relatedFields = {
										...relatedFields,
										address2: e.target.value
									};
									this.updateFormFieldConfig(section._id, field._id, 'relatedFields', updatedConfig.relatedFields);
								}}
							/>

							<InputField
								label="City Field ID"
								value={relatedFields.city}
								placeholder="e.g., city_field_id"
								onChange={(e) => {
									const updatedConfig = field.moduleConfiguration || {};
									updatedConfig.relatedFields = {
										...relatedFields,
										city: e.target.value
									};
									this.updateFormFieldConfig(section._id, field._id, 'relatedFields', updatedConfig.relatedFields);
								}}
							/>

							<InputField
								label="State Field ID"
								value={relatedFields.state}
								placeholder="e.g., state_field_id"
								onChange={(e) => {
									const updatedConfig = field.moduleConfiguration || {};
									updatedConfig.relatedFields = {
										...relatedFields,
										state: e.target.value
									};
									this.updateFormFieldConfig(section._id, field._id, 'relatedFields', updatedConfig.relatedFields);
								}}
							/>


							<InputField
								label="State 2 Field ID"
								value={relatedFields.state2}
								placeholder="e.g., state2_field_id"
								onChange={(e) => {
									const updatedConfig = field.moduleConfiguration || {};
									updatedConfig.relatedFields = {
										...relatedFields,
										state2: e.target.value
									};
									this.updateFormFieldConfig(section._id, field._id, 'relatedFields', updatedConfig.relatedFields);
								}}
							/>


							<InputField
								label="Zip Field ID"
								value={relatedFields.zip}
								placeholder="e.g., zip_field_id"
								onChange={(e) => {
									const updatedConfig = field.moduleConfiguration || {};
									updatedConfig.relatedFields = {
										...relatedFields,
										zip: e.target.value
									};
									this.updateFormFieldConfig(section._id, field._id, 'relatedFields', updatedConfig.relatedFields);
								}}
							/>

							<InputField
								label="Country Field ID"
								value={relatedFields.country}
								placeholder="e.g., country_field_id"
								onChange={(e) => {
									const updatedConfig = field.moduleConfiguration || {};
									updatedConfig.relatedFields = {
										...relatedFields,
										country: e.target.value
									};
									this.updateFormFieldConfig(section._id, field._id, 'relatedFields', updatedConfig.relatedFields);
								}}
							/>
						</div>
					);
					break;
				case 'phoneNumber':
					configElement = (
						<div className="formFieldContainer">
							<ToggleField
								label="Enable International Phone Numbers"
								checked={field.moduleConfiguration?.enableInternational || false}
								icons={false}
								onChange={(e) => {
									const updatedConfig = field.moduleConfiguration || {};
									updatedConfig.enableInternational = e.target.checked;
									this.updateFormField(section._id, field._id, 'moduleConfiguration', updatedConfig);
								}}
							/>
							<InputField
								label="Default Country Code"
								value={field.moduleConfiguration?.defaultCode || ''}
								placeholder="e.g., US"
								onChange={(e) => {
									const updatedConfig = field.moduleConfiguration || {};
									updatedConfig.defaultCode = e.target.value;
									this.updateFormField(section._id, field._id, 'moduleConfiguration', updatedConfig);
								}}
							/>
						</div>
					);
					break;
				default:
					break;
			}
			let isBaseField = false;
			isBaseField = field.objectClass === 'baseInput';

			let isReservedDateTimes = field.id === 'reservedDateTimes';
			return (
				<Fragment key={`editing-${section._id}-${field._id}`
				}>

					<div className="flex aic jcsb mb-20  w-100">
						<h1>{field.label}</h1>
					</div>
					{
						!isModuleField && (
							<div className="formEditField">
								<div>
									<InputField
										label="Label"
										value={field.label}
										type="text"
										onChange={(e) => this.updateFormField(section._id, field._id, 'label', e.target.value)}
									/>
									{attendeeFieldType === 'base' && (
										<p className="small proTipColor" style={{ marginTop: -5, marginBottom: 15 }}>
											<span className='las la-user'></span>{currentCustomField.label || field.attendeeField?.propertyName || 'Not mapped'}
										</p>
									)}
								</div>
								{attendeeFieldType !== 'base' && (
									<SelectField
										label="Mapped Field"
										value={currentCustomField}
										options={this.state.customFields}
										classes="w-100"
										actionMessage={
											Object.keys(currentCustomField).length === 0 ?
												<div
													className="se-blue c-pointer"
													onClick={() =>
														this.setState({
															addCustomFieldModalOpen: true,
															focusedField: {
																section: section._id,
																nested: false,
																id: field._id,
																label: field.label
															}
														})}
												>
													Create new field
												</div> : <div
													className="se-red c-pointer"
													onClick={() => {

														this.setState({
															focusedField: {
																section: section._id,
																nested: false,
																id: field._id,
																label: field.label
															}
														});

														this.updateFormField(section._id, field._id, 'id', '');
														this.updateFormField(section._id, field._id, 'attendeeField', {
															...field.attendeeField,
															propertyName: '',
															propertyType: 'custom'
														});
														this.updateFormField(section._id, field._id, 'objectClass', 'mappedInput');
													}
													}

												>
													Unmap field
												</div>
										}
										actionOn={true/* attendeeFieldType == 'unattached' */}
										formatGroupLabel={(data) => (
											<div style={groupStyles}>
												<span>{data.label}</span>
												<span style={groupBadgeStyles}>{data.options.length}</span>
											</div>
										)}
										onChange={(data) => {
											this.updateFormField(section._id, field._id, 'id', data._id);
											this.updateFormField(section._id, field._id, 'attendeeField', {
												...field.attendeeField,
												propertyName: data._id,
												propertyType: 'custom'
											});
											this.updateFormField(section._id, field._id, 'objectClass', 'mappedInput');
										}}
									/>
								)}
								<RichTextEditor
									id={`field-${field._id.replace('*', '')}-description`}
									label="Description"
									value={field.description}
									options={['image']}
									onChange={(e) => {
										this.updateFormField(section._id, field._id, 'description', e)
									}}
									placeholder="Description"
									customFields={this.props.customFields}
								/>
								<div className="flex-ac">
									{<SelectField
										label="Field Type"
										classes="w-100"
										value={fieldTypes.find((t) => t.value == field.type)}
										options={fieldTypes}
										onChange={(e) => {
											this.updateFormField(section._id, field._id, 'type', e.value);
										}}
										style={{ marginRight: 10 }}
									/>}
									<SelectField
										classes="w-100"
										label="Column Size"
										value={columnSizeOptions.find((o) => o.value == field.columnClass)}
										options={columnSizeOptions}
										onChange={(e) => this.updateFormField(section._id, field._id, 'columnClass', e.value)}
									/>
								</div>
								{!isReservedDateTimes && (
									<div className="flex-ac">

										{field.type === 'select' || field.type === 'nestedSections' ?
											<SelectField
												classes="mr-10"
												label="Default Value"
												value={field.defaultValue ? { value: field.defaultValue, label: field.defaultValue } : ''}
												options={[{ label: '', value: '' }, ...field.options.map((o) => ({ label: o, value: o }))]}
												onChange={(e) => this.updateFormField(section._id, field._id, 'defaultValue', e.value)}
											/> :

											<InputField
												label="Default Value"
												value={field.defaultValue}
												type={field.type === 'date' ? 'date' : 'text'}
												inputIcon={field.type === 'date' && <i className="lar la-calendar" />}

												onChange={(e) => {
													this.updateFormField(section._id, field._id, 'defaultValue', e.target.value)
												}
												}
												classes="mr-10"
											/>}

										<InputField
											label="PlaceHolder"
											type={field.type === 'date' ? 'date' : 'text'}
											inputIcon={field.type === 'date' && <i className="lar la-calendar" />}
											value={field.placeholder}
											onChange={(e) =>
												this.updateFormField(section._id, field._id, 'placeholder', e.target.value)}
										/>
									</div>
								)}
								{!isReservedDateTimes && <ToggleField
									label="Required"
									checked={field.required}
									icons={false}
									onChange={(e) => {
										const r = e.target.checked;
										this.updateFormField(section._id, field._id, 'required', r);
									}}
								/>}
								<ToggleField
									label="Hide Field"
									checked={field.hidden}
									icons={false}
									onChange={(e) => {
										const r = e.target.checked;
										this.updateFormField(section._id, field._id, 'hidden', r);
									}}
								/>
								{field.type == 'number' && <ToggleField
									label="Allow Negative Values"
									checked={field.moduleConfiguration && field.moduleConfiguration.allowNegative}
									icons={false}
									onChange={(e) => {
										const r = e.target.checked;
										const updatedModuleConfiguration = field.moduleConfiguration || {};
										updatedModuleConfiguration.allowNegative = r;
										this.updateFormField(section._id, field._id, 'moduleConfiguration', updatedModuleConfiguration);
									}}
								/>}
								{!isReservedDateTimes && (field.type !== 'color' && field.type !== 'nestedSections') && (
									<ToggleField
										label="Disable Field"
										checked={field.disabled}
										icons={false}
										onChange={(e) => {
											const r = e.target.checked;
											this.updateFormField(section._id, field._id, 'disabled', r);
										}}
									/>
								)}
							</div>
						)
					}
					{configElement}

					<button className='mt-a neu w-100 mb-20 danger'
						onClick={() => {
							this.removeFormField(section._id, field._id);
						}}>Remove Field</button>
				</Fragment >
			);
		}
	};

	renderFormFieldConfig = (section, field, configField) => {
		let configElement;

		switch (configField.type) {
			case 'list':
				configElement = (
					<div>
						<div className="labelContainer">
							<div className="formFieldLabel">Options</div>
							<div
								className="se-blue c-pointer "
								onClick={() => {
									const updatedOptions = configField.options;
									updatedOptions.push('');
									this.updateFormFieldConfigField(
										section._id,
										field._id,
										configField._id,
										'options',
										updatedOptions
									);
								}}
							>
								+ Add option
							</div>
						</div>
						<div className="formEditlistOptions">
							{configField.options.map((opt, oi) => {
								return (
									<div className="flex-ac mb-10">
										<InputField
											value={opt}
											classes="m-0"
											onChange={(e) => {
												const updatedOptions = configField.options;
												updatedOptions[oi] = e.target.value;
												this.updateFormFieldConfigField(
													section._id,
													field._id,
													configField._id,
													'options',
													updatedOptions
												);
											}}
										/>
										<i
											className="las la-times ml-10 c-pointer"
											onClick={() => {
												const updatedOptions = configField.options.filter((op) => op !== opt);
												this.updateFormFieldConfigField(
													section._id,
													field._id,
													configField._id,
													'options',
													updatedOptions
												);
											}}
										/>
									</div>
								);
							})}
						</div>
					</div>
				);
				break;
			case 'email':
				configElement = <div />;
				break;
			case 'multipleCheck':
				configElement = (
					<div>
						<div className="labelContainer">
							<div className="formFieldLabel">Options</div>

							<div
								className="se-blue c-pointer "
								onClick={() => {
									const updatedOptions = configField.options;
									updatedOptions.push('');
									this.updateFormFieldConfigField(
										section._id,
										field._id,
										configField._id,
										'options',
										updatedOptions
									);
								}}
							>
								+ Add option
							</div>
						</div>
						<div className="formEditlistOptions">
							{configField.options.map((opt, oi) => {
								return (
									<div className="flex-ac mb-10">
										<InputField
											value={opt}
											classes="m-0"
											onChange={(e) => {
												const updatedOptions = configField.options;
												updatedOptions[oi] = e.target.value;
												this.updateFormFieldConfigField(
													section._id,
													field._id,
													configField._id,
													'options',
													updatedOptions
												);
											}}
										/>
										<i
											className="las la-times ml-10 c-pointer"
											onClick={() => {
												const updatedOptions = configField.options.filter((op) => op !== opt);
												this.updateFormFieldConfigField(
													section._id,
													field._id,
													configField._id,
													'options',
													updatedOptions
												);
											}}
										/>
									</div>
								);
							})}
						</div>
					</div>
				);
				break;
			case 'select':
				configElement = (
					<div className="formEditlistOptions">
						<div className="labelContainer">
							<div className="formFieldLabel">Options</div>

							<div
								className="se-blue c-pointer "
								onClick={() => {
									const updatedOptions = configField.options;
									updatedOptions.push('');
									this.updateFormFieldConfigField(
										section._id,
										field._id,
										configField._id,
										'options',
										updatedOptions
									);
								}}
							>
								+ Add option
							</div>
						</div>
						<div>
							{configField.options &&
								configField.options.map((opt, oi) => {
									return (
										<div className="flex-ac mb-10">
											<InputField
												value={opt}
												classes="m-0"
												onChange={(e) => {
													const updatedOptions = configField.options;
													updatedOptions[oi] = e.target.value;
													this.updateFormFieldConfigField(
														section._id,
														field._id,
														configField._id,
														'options',
														updatedOptions
													);
													this.updateFormField(
														section._id,
														field._id,
														'defaultValue',
														''
													);
												}}
											/>
											<i
												className="las la-times ml-10 c-pointer"
												onClick={() => {
													const updatedOptions = configField.options.filter(
														(op) => op !== opt
													);
													this.updateFormFieldConfigField(
														section._id,
														field._id,
														configField._id,
														'options',
														updatedOptions
													);
													this.updateFormField(
														section._id,
														field._id,
														'defaultValue',
														''
													);
												}}
											/>
										</div>
									);
								})}
						</div>
					</div>
				);
				break;
			case 'rating':
				configElement = <div />;
				break;
			default:
				break;
		}
		return configElement ? (
			<div className="fieldTypeConfig mt-10">
				<label className="calibreSemiBold">Config</label>
				{configElement}
			</div>
		) : (
			''
		);
	};

	updateSelected = (selectedIndex) => {
		this.setState({ selectedSection: selectedIndex });
	};

	removePage = (index) => {
		const { currentPage } = this.state;
		const { editedForm } = this.props;
		editedForm.pages.splice(index, 1);
		const cp = currentPage > editedForm.pages.length - 1 ? editedForm.pages.length - 1 : currentPage;

		this.props.updateForm(editedForm);
		this.setState({ currentPage: cp });
	};

	addPage = () => {
		const { editedForm } = this.props;
		editedForm.pages.push({
			_id: `page_${new Date().getTime()}`,
			sections: []
		});
		this.props.updateForm(editedForm);
		this.setState({ currentPage: editedForm.pages.length - 1 });
	}

	nextPage = () => {
		const { currentPage } = this.state;
		const { editedForm } = this.props;
		if (currentPage < editedForm.pages.length - 1) {
			this.setState({ currentPage: currentPage + 1 });
		}
	};

	prevPage = () => {
		const { currentPage } = this.state;
		if (currentPage > 0) {
			this.setState({ currentPage: currentPage - 1 });
		}
	};

	toggleDetailsOpen = () => {
		const { detailsOpen } = this.state;
		this.setState({ detailsOpen: !detailsOpen, detailsExpanded: false });
	};

	toggleDetailsExpanded = () => {
		const { detailsExpanded } = this.state;
		this.setState({ detailsExpanded: !detailsExpanded });
	};

	toggleSection = (section) => {
		switch (section) {
			case 'editor':
				const editorState = { detailsOpen: !this.state.detailsOpen, componentLibraryIsOpen: false, historyOpen: false };
				if (this.state.historyOpen && this.state.savedFormBeforeHistory) {
					this.props.updateForm(this.state.savedFormBeforeHistory);
					editorState.savedFormBeforeHistory = null;
					editorState.previewedVersionId = null;
					editorState.renderOnly = false;
					editorState.hasPendingEdits = false;

					// Notify parent that history is closed
					if (this.props.onHistoryStateChange) {
						this.props.onHistoryStateChange(false, false);
					}
				}
				this.setState(editorState);
				break;
			case 'library':
				const libraryState = { componentLibraryIsOpen: !this.state.componentLibraryIsOpen, detailsOpen: false, historyOpen: false };
				if (this.state.historyOpen && this.state.savedFormBeforeHistory) {
					this.props.updateForm(this.state.savedFormBeforeHistory);
					libraryState.savedFormBeforeHistory = null;
					libraryState.previewedVersionId = null;
					libraryState.renderOnly = false;
					libraryState.hasPendingEdits = false;

					// Notify parent that history is closed
					if (this.props.onHistoryStateChange) {
						this.props.onHistoryStateChange(false, false);
					}
				}
				this.setState(libraryState);
				break;
			case 'history':
				const newHistoryOpen = !this.state.historyOpen;
				const baseState = {
					historyOpen: newHistoryOpen,
					detailsOpen: false,
					componentLibraryIsOpen: false
				};

				if (newHistoryOpen) {
					// When opening history, preserve pending edits and temporarily hide them from parent
					const hasUnsavedChanges = JSON.stringify(this.props.editedForm) !== JSON.stringify(this.props.form);
					baseState.hasPendingEdits = hasUnsavedChanges;
					baseState.savedFormBeforeHistory = JSON.parse(JSON.stringify(this.props.editedForm));

					// Reset to original form so parent sees no pending changes during history browsing
					if (hasUnsavedChanges) {
						this.props.updateForm(this.props.form);
					}

					// Notify parent that history is open with pending edits info
					if (this.props.onHistoryStateChange) {
						this.props.onHistoryStateChange(true, hasUnsavedChanges);
					}

					if (!this.state.versionsLoading && this.props.editedForm && this.props.editedForm._id) {
						this.setState({ versionsLoading: true });
						const { orgId, eventId, editedForm } = this.props;
						API().get(`Organizations/${orgId}/events/${eventId}/forms/${editedForm._id}/versions/`).then((res) => {
							this.setState({ versions: res.data, versionsLoading: false }, () => {
								if (res.data.length > 0) {
									this.loadVersionPreview(res.data[0]);
								}
							});
						}).catch(() => {
							this.setState({ versionsLoading: false });
						});
					}
				} else {
					// When closing history, restore saved state and pending edits
					baseState.previewedVersionId = null;
					baseState.renderOnly = false;
					if (this.state.savedFormBeforeHistory) {
						this.props.updateForm(this.state.savedFormBeforeHistory);
						baseState.savedFormBeforeHistory = null;
					}
					baseState.hasPendingEdits = false;

					// Notify parent that history is closed
					if (this.props.onHistoryStateChange) {
						this.props.onHistoryStateChange(false, false);
					}
				}

				this.setState(baseState);
				break;
			default:
				break;

		}
	}
	loadVersionPreview = (version) => {
		// Load version snapshot for preview (not saving)
		if (version.snapshot) {
			const updatedForm = { ...this.props.editedForm };
			// The snapshot might contain full form or just pages
			if (version.snapshot.pages) {
				updatedForm.pages = JSON.parse(JSON.stringify(version.snapshot.pages));
			} else if (Array.isArray(version.snapshot)) {
				// Handle case where snapshot is directly the pages array
				updatedForm.pages = JSON.parse(JSON.stringify(version.snapshot));
			}
			this.setState({
				previewedVersionId: version._id || version.versionNumber,
				renderOnly: true,
				currentPage: 0
			}, () => {
				this.props.updateForm(updatedForm);
			});
		}
	};

	revertToVersion = (version) => {
		// Actually revert and save the form to this version
		if (window.confirm(`Are you sure you want to revert to Version ${version.versionNumber || 'this version'}?`)) {
			if (version.snapshot) {
				const updatedForm = { ...this.props.editedForm };
				// The snapshot might contain full form or just pages
				if (version.snapshot.pages) {
					updatedForm.pages = JSON.parse(JSON.stringify(version.snapshot.pages));
				} else if (Array.isArray(version.snapshot)) {
					updatedForm.pages = JSON.parse(JSON.stringify(version.snapshot));
				}
				this.setState({ previewedVersionId: null, renderOnly: false, historyOpen: false, hasPendingEdits: false, savedFormBeforeHistory: null, currentPage: 0 }, () => {
					this.props.updateForm(updatedForm);
					if (this.props.onHistoryStateChange) {
						this.props.onHistoryStateChange(false, false);
					}
					this.props.saveForm();
				});
			}
		}
	};

	refetchVersions = () => {
		const { orgId, eventId, editedForm } = this.props;
		if (editedForm && editedForm._id) {
			API().get(`Organizations/${orgId}/events/${eventId}/forms/${editedForm._id}/versions/`).then((res) => {
				this.setState({
					versions: res.data,
					previewedVersionId: res.data.length > 0 ? res.data[0]._id || res.data[0].versionNumber : null
				});
			}).catch(() => {
				// Handle error silently or show notification if needed
			});
		}
	};

	returnToCurrent = () => {
		// Return to the current unsaved state (before opening history)
		this.setState({
			previewedVersionId: null,
			renderOnly: false
		}, () => {
			if (this.state.savedFormBeforeHistory) {
				this.props.updateForm(this.state.savedFormBeforeHistory);
			}
		});
	};

	handleJoyrideCallback = (data) => {
		const { status } = data;
		const finishedStatuses = ['finished', 'skipped'];
		if (finishedStatuses.includes(status)) {
			this.setState({ runPageTutorial: false });
		}
	};

	startTour = () => {
		setTimeout(() => {
			this.setState({ runPageTutorial: true });
		}, 1000);
	};

	handleUploadOptions = (options) => {
		const { uploadTargetSection, uploadTargetField } = this.state;
		if (uploadTargetSection && uploadTargetField) {
			const updatedOptions = [...uploadTargetField.options, ...options];
			this.updateFormField(uploadTargetSection._id, uploadTargetField._id, 'options', updatedOptions);
		}
		this.setState({
			uploadOptionsModalOpen: false,
			uploadTargetSection: null,
			uploadTargetField: null
		});
	};

	render() {
		const {
			currentPage,
			focusedButtonId,
			focusedSectionId,
			fieldSelected,
			addCustomFieldModalOpen,
			detailsOpen,
			componentLibraryIsOpen,
			focusedFieldIndex,
			runPageTutorial
		} = this.state;
		const { editedForm, baseFieldList, customFieldList } = this.props;
		let focusedFieldId = this.state.focusedFieldId;
		let focusedField, focusedSection;
		if (focusedSectionId) {
			focusedSection = editedForm.pages[currentPage].sections.find((s) => s._id == focusedSectionId);
			if (focusedSection) {
				if (focusedFieldId) {
					focusedField = focusedSection.fields.find((f) => f._id == focusedFieldId);
					if (!focusedField) {
						focusedField = focusedSection.fields[focusedFieldIndex];
					}
				} else if (focusedButtonId) {
					focusedField = focusedSection.buttons.find((b) => b._id == focusedButtonId);
				}
				if (focusedFieldId && focusedFieldId.startsWith('^')) {
					focusedFieldId = focusedFieldId.substring(1);
				}
			}
		}
		const cp = editedForm.pages ? editedForm.pages[currentPage] : {};
		return (
			<Fragment>
				<Joyride
					callback={this.handleJoyrideCallback}
					continuous
					run={runPageTutorial}
					floaterProps={{
						styles: {
							wrapper: {
								zIndex: 2000
							},
						},
					}}
					scrollOffset={64}
					scrollToFirstStep
					showProgress={false}
					hideCloseButton={false}
					showSkipButton
					steps={tutorials['FormEditor']}
					styles={{
						options: {
							zIndex: 2000,
						},
						buttonNext: {
							background: 'var(--primary)',
							border: 'var(--primary--border)',
							color: 'var(--white)',
							fontWeight: 600,
							padding: '10px 15px',
							fontSize: 14,
							width: 65,
							height: 30,
						},
						buttonSkip: {
							background: 'var()',
							border: 'var(--primary--border)',
							color: 'var(--primary)',
							fontWeight: 600,
							padding: '10px 15px',
							fontSize: 14,
						},
						buttonBack: {
							color: "#007bff",
						},
					}}
				/>
				<DragDropContext onDragEnd={this.onDragEnd}>

					<div className='flex jcsb w-100 h-100'>
						<div className='w-100 flex column aic'>
							{!this.props.limitPages ? <div className='formPageNav mt-20 mb-10'>
								<button className={`neu p-7 ${currentPage === 0 ? 'disabled' : ''}`} onClick={this.prevPage}><i className='las la-angle-left'></i></button>
								<div>
									Page {currentPage + 1} of {editedForm.pages.length}

								</div>
								{currentPage === editedForm.pages.length - 1 || editedForm.pages.length === 0 ? (!this.state.renderOnly && <button className={`prim`} onClick={this.addPage}><i className='las la-plus mr-10'></i>New Page</button>) : <button className={`neu p-7`} onClick={this.nextPage}><i className='las la-angle-right'></i></button>}
							</div> : <div className=' mt-20 mb-10'>

							</div>}
							{cp && (<div className='formCanvas'>
								<div className="formContainer" key={`currentPage-${currentPage}`}>
									<div className={this.state.renderOnly ? '' : 'c-pointer'} onClick={() => {
										if (this.state.renderOnly) return;
										this.setState({ focusedSectionId: null, focusedFieldId: null, focusedFieldIndex: null, focusedButtonId: null, detailsOpen: true, componentLibraryIsOpen: false })
									}}>

										<h2 className='mb-10'>{cp.title && cp.title !== '' ? cp.title : <span className='lightBody'>{!this.state.renderOnly ? 'Click to Edit Title' : ''}</span>}</h2>
										<div className='formTitleBreakBar'></div>
										{cp.subtitle && cp.subtitle !== '' ? <p className='mb-20 mt-20' dangerouslySetInnerHTML={{ __html: cp.subtitle }}></p> : <p className='mb-20 mt-20 lightBody'>{!this.state.renderOnly ? 'Click to Edit Subtitle' : ''}</p>}

									</div>
									<div className="labelContainer">
										<div className="formFieldLabel">Sections</div>
									</div>
									<Droppable
										droppableId={`${cp._id}`}
										key={`drop-${cp._id}`}
										type="section"
									>
										{(provided3, snapshot) => (
											<div
												className="formEditorPageContainer"
												ref={provided3.innerRef}
												key={`drop-${cp._id}`} >
												{cp.sections.map((section, si) => {
													return (
														<Draggable key={section._id} draggableId={`drag-${section._id}`} index={si} isDragDisabled={this.state.renderOnly}>
															{(provided2, snapshot2) => (
																<div

																	className={`formEditorSectionContainer ${focusedSectionId == section._id ? 'highlight' : ''} `}
																	ref={provided2.innerRef}
																	{...provided2.draggableProps}
																	{...provided2.dragHandleProps}
																>
																	<div className={this.state.renderOnly ? '' : 'c-pointer'} onClick={() => {
																		if (this.state.renderOnly) return;
																		this.setState({ focusedSectionId: section._id, focusedFieldId: null, focusedFieldIndex: null, focusedButtonId: null, detailsOpen: true, componentLibraryIsOpen: false })
																	}}
																		key={`section-${section._id}`}>

																		<div className='flex aic jcc w-100 g-pointer' style={{ marginTop: -10 }}>
																			<i className='las la-braille' style={{ transform: 'rotate(90deg)' }}></i>
																		</div>

																		{section.title && section.title !== '' ? <h4 className='mb-10' dangerouslySetInnerHTML={{ __html: section.title }}></h4> : <h4 className='mb-10 lightBody'>Click to Edit Section Title</h4>}
																		<div className="labelContainer">
																			<div className="formFieldLabel">Fields</div>
																		</div>
																	</div>
																	<Droppable
																		droppableId={`${section._id}`}
																		key={`drop-${section._id}`}
																		type="field"
																	>
																		{(provided, snapshot) => (
																			<div
																				className='formEditorFieldContainer'
																				ref={provided.innerRef}>

																				{section.fields && (
																					<div className="flex-wrap m-0">
																						{section.fields.map((field, index) => (
																							<DraggableField
																								isDragDisabled={this.state.renderOnly}
																								focusOnField={(
																									fField,
																									fSection
																								) => {
																									if (this.state.renderOnly) return;
																									this.setState({
																										formEditorTabSelected: 1,
																										fieldSelected: true,
																										detailsOpen: true,
																										componentLibraryIsOpen: false,
																										focusedFieldId: fField._id,
																										focusedFieldIndex: index,
																										focusedSectionId:
																											fSection._id
																									});
																								}}
																								customFields={this.props.fields.map(
																									(category) => {
																										const title =
																											category.title;
																										const headers = category.fields.map(
																											(header) => {
																												const name =
																													header.fieldName;
																												return {
																													_id: header._id,
																													category: title,
																													categoryId:
																														category._id,
																													value: name,
																													label: name
																												};
																											}
																										);
																										return {
																											label: title,
																											value: category._id,
																											options: headers
																										};
																									}
																								)}
																								key={field._id}
																								field={field}
																								focused={focusedFieldId == field._id}
																								section={section}
																								index={index}
																								updateFormField={
																									this.updateFormField
																								}
																								updateNestedFormField={
																									this.updateNestedFormField
																								}
																								removeFormField={
																									this.removeFormField
																								}
																							/>
																						))}
																					</div>
																				)}
																				{section.fields.length === 0 && (<div style={{ padding: 15, textAlign: 'center', color: '#999' }}>Add fields by dragging over from Library</div>)}
																				{provided.placeholder}
																			</div>
																		)}
																	</Droppable>
																</div>
															)}
														</Draggable>
													);
												})}
												{cp.sections.length === 0 && (<div style={{ padding: 20, textAlign: 'center', color: '#999' }}>Create a section to add fields</div>)}
												{provided3.placeholder}
											</div>)}
									</Droppable>
									<button
										className="prim w-100 mt-20"
										onClick={this.addNewSection}
									>
										<i className="las la-plus mr-10" />
										New Section
									</button>
								</div>
							</div>)}
						</div>
						<div className={`designerPanel p-0 right ${!componentLibraryIsOpen ? 'closed' : ''} `} style={{ overflowX: 'hidden', position: 'relative' }}>
							<div style={{ padding: 25 }}>

								<button onClick={() => this.toggleSection('library')} style={{ padding: 7, marginLeft: -7, marginTop: -7, fontSize: 16 }}><i className='las la-times'></i></button>

								<div className='mb-20'>
									<h1>Form Fields</h1>
								</div>
								<div className={`formInputsSection selected`}>
									<Droppable droppableId={`drop-attendee-fields`} type="field">
										{(provided, snapshot) => (
											<div
												ref={provided.innerRef}
												className="formFieldsContainer"
											>
												<p className='gray small mb-10'>Attendee Fields</p>
												{baseFieldList.map((field, index) => (<><DraggableCustomField key={field._id} field={field} index={index} /><hr /></>

												))}

												{customFieldList && customFieldList.length > 0 && (
													<>
														<p className='gray small mt-50 mb-10'>Custom Fields</p>
														{customFieldList.length === 0 && (<h3>No Custom Fields</h3>)}
														{customFieldList.map((field, index) => (
															<>
																<DraggableCustomField key={field._id} field={field} index={index} />
																<hr />
															</>
														))}
													</>
												)}

												{(!customFieldList || customFieldList.length === 0) && (
													<>
														<h3>No Custom Fields</h3>
														<div
															className="se-blue categoryHeader c-pointer"
															onClick={() =>
																this.setState({
																	addCustomFieldModalOpen: true,
																	focusedField: {
																		section: null,
																		nested: false,
																		id: null,
																		label: ''
																	}
																})}
														>
															Create New Field
														</div>
														{customFieldList && customFieldList.length > 0 && customFieldList.map((field, index) => (<><DraggableCustomField key={field._id} field={field} index={index} /><hr /></>
														))}
													</>
												)}

												<p className='gray small mt-50 mb-10'>All Fields Type</p>
												{unattachedFieldList.map((field, index) => (<><DraggableCustomField key={field._id} field={field} index={index} /><hr /></>

												))}
												{provided.placeholder}
											</div>
										)}
									</Droppable>
								</div>
							</div>
						</div>

						<div className={`designerPanel p-0 right ${!detailsOpen ? 'closed' : ''} `} style={{ overflow: 'hidden', position: 'relative' }} >
							<div style={{ overflowX: 'hidden', padding: 25, paddingBottom: 60, maxHeight: '100%' }} className={`formInputsSection selected`}>
								<button onClick={() => this.toggleSection('editor')} style={{ padding: 7, marginLeft: -7, marginTop: -7, fontSize: 16 }}><i className='las la-times'></i></button>
								{fieldSelected && focusedFieldId && focusedSection ? (
									<Fragment>
										{this.renderFocusedFieldConfig(focusedSection, focusedField)}
									</Fragment>
								) : focusedSection ? (
									<Fragment>

										<div className="flex aic mb-20  w-100">
											<h1>Section</h1>
										</div>

										<RichTextEditor
											id={`section-${currentPage}-${focusedSectionId.includes('^') ? focusedSectionId.replace('^', '') : focusedSectionId}`}
											value={focusedSection.title}
											options={['image']}
											onChange={(e) => {
												const eForm = { ...this.props.editedForm };
												eForm.pages[currentPage].sections[
													editedForm.pages[currentPage].sections.findIndex(
														(section) =>
															section._id == focusedSectionId
													)
												].title = e;
												this.props.updateForm(eForm);

											}}
											placeholder="Section Title"
											label="Section Title"
											key={`section-${currentPage}-${focusedSectionId.includes('^') ? focusedSectionId.replace('^', '') : focusedSectionId}`}
											customFields={this.props.customFields}

										/>
										<div className='mt-a'>
											<div className='flex aic jcsb mb-10'>
												<button
													className={`min ${currentPage === 0 ? 'disabled' : ''}`}
													onClick={() =>
														this.moveSection(
															'sectionUp',
															focusedSection._id
														)}
												>
													Move to Previous Page
												</button>

												<button
													className={`min ${currentPage !== editedForm.pages.length - 1 ? '' : 'disabled'}`}
													onClick={() =>
														this.moveSection(
															'sectionDown',
															focusedSection._id
														)}
												>
													Move to Next Page
												</button></div>
											<button
												className='neu w-100 danger'
												onClick={() =>
													this.removeSection(
														focusedSection._id
													)}
											>
												Delete Section
											</button>
										</div>
									</Fragment>
								) : (
									<Fragment>
										<div className="flex aic  mb-20 w-100">
											<h1>Page {currentPage + 1}</h1>
										</div>
										<InputField
											value={cp ? cp.title : ''}
											label="Page Title"
											placeholder="Page Title"
											key={`title-${currentPage}`}
											onChange={(e) => {
												const eForm = { ...this.props.editedForm };
												eForm.pages[currentPage].title = e.target.value;
												this.props.updateForm(eForm);
											}}
										/>
										<RichTextEditor
											id={`section-${currentPage}-${'pageSubtitle'}`}
											key={`section-${currentPage}-${'pageSubtitle'}`}
											options={['image']}
											label="Page Subtitle"
											value={cp ? cp.subtitle : ''}
											onChange={(e) => {
												const eForm = { ...this.props.editedForm };
												eForm.pages[currentPage].subtitle = e;
												this.props.updateForm(eForm);
											}}
											placeholder="Page Subtitle"
											customFields={this.props.customFields}
										/>

										{!this.props.limitPages && this.props.editedForm.pages.length > 1 && <div className='mt-a w-100'>
											<button
												className='neu w-100 danger'
												onClick={() =>
													this.removePage(currentPage)}
											>
												Delete Page
											</button>
										</div>}
									</Fragment>
								)}
							</div>
						</div>

						<div className={`designerPanel p-0 right ${!this.state.historyOpen ? 'closed' : ''}`} style={{ overflowX: 'hidden', position: 'relative' }} >
							<div style={{ overflowX: 'hidden', padding: 25, paddingBottom: 60, maxHeight: '100%' }} className={`formInputsSection selected`}>
								<button onClick={() => this.toggleSection('history')} style={{ padding: 7, marginLeft: -7, marginTop: -7, fontSize: 16 }}><i className='las la-times'></i></button>
								<h1 className='mb-10'>History</h1>
								<p className='mb-20'>
									View and manage previous versions of this form. Preview each version before deciding to revert to it. Your current changes will be preserved and you can return to the editor to continue working.
								</p>
								{this.state.versionsLoading ? (
									<LoadingWizard loading={this.state.versionsLoading} text="Loading versions..." />
								) : this.state.versions.length === 0 ? (
									<div className="explainerBox">
										<div>
											<h2 className='mb-5'>No Versions Yet</h2>
											<p>Once edits are made you will be able to see and restore previous versions, allowing you to preview each before having to apply.</p>
										</div>
									</div>
								) : (
									<div className="history-list">
										{(() => {
											const { groups, sortedKeys } = groupVersionsByDate(this.state.versions);
											return sortedKeys.map((groupKey, i) => {
												const group = groups[groupKey];
												return (
													<div key={groupKey} className="version-group mb-20">
														<h4 className="version-header mb-10" style={{ color: '#9c9c9c', paddingBottom: '5px' }}>
															{group.header}
														</h4>
														{group.versions.map((version, index) => {
															const isPreviewed = (version._id || version.versionNumber) === this.state.previewedVersionId;
															const globalIndex = this.state.versions.findIndex(v => v._id === version._id);
															const fieldCount = version.snapshot && Array.isArray(version.snapshot) ?
																version.snapshot.reduce((total, page) => {
																	if (page.sections && Array.isArray(page.sections)) {
																		return total + page.sections.reduce((sectionTotal, section) => {
																			return sectionTotal + (section.fields && Array.isArray(section.fields) ? section.fields.length : 0);
																		}, 0);
																	}
																	return total;
																}, 0) : 0;
															return (
																<div
																	key={version._id || index}
																	className={`history-item mb-10 p-10 rounded c-pointer `}
																	onClick={() => {
																		const versionId = version._id || version.versionNumber;
																		if (versionId !== this.state.previewedVersionId) {
																			this.loadVersionPreview(version);
																		}
																	}}
																	style={{ cursor: 'pointer', border: isPreviewed ? '1px solid var(--primary)' : '1px solid var(--border)' }}
																>
																	<div className="flex jcsb ">
																		<div>
																			<h3 className="mb-10">{version.createdAt ? moment(version.createdAt).format('MMM D, YYYY h:mm A') : 'Unknown date'}</h3>
																			{i == 0 && index == 0 && (
																				<p className="small mb-5" style={{ color: 'var(--primary)' }}>Current Version</p>
																			)}
																			<p className="small mb-5" style={{ color: '#090505ff' }}>{`${fieldCount} Field${fieldCount !== 1 ? 's' : ''}`}</p>
																			<p className="small" style={{ color: '#9c9c9c' }}>Edited by: {version.editedBy || 'Unknown'}</p>
																		</div>
																		{globalIndex > 0 && (
																			<UncontrolledDropdown inNavbar>
																				<DropdownToggle className="columnToggle flex-ac" style={{ minHeight: '0px' }}>
																					<i className="las la-ellipsis-h" />
																				</DropdownToggle>
																				<DropdownMenu
																					right
																					className="columnDropdown p-20"
																				>
																					<h5 className='mb-10'>Options</h5>
																					<DropdownItem
																						className="dropdownItem"
																						onClick={(e) => {
																							e.stopPropagation();
																							this.revertToVersion(version);
																						}}
																					>
																						<i className="las la-undo mr-10" />Revert
																					</DropdownItem>
																				</DropdownMenu>
																			</UncontrolledDropdown>
																		)}
																	</div>
																</div>
															);
														})}
													</div>
												);
											});
										})()}
									</div>
								)}
							</div>
						</div>

						<div className='designerPanel side p-0' style={{ overflow: 'hidden', position: 'relative', minWidth: 100, width: 100 }} >
							<div className='flex column jcc w-100'>
								<div className={`flex column jcc aic c-pointer editor ${detailsOpen ? 'selectedSection' : ''} `} style={{ height: 100 }} onClick={() => {
									this.toggleSection('editor');
								}}>
									<i className='las la-edit mb-10' style={{ fontSize: 30 }}></i>
									<h5>Editor</h5></div>
								<div className={`flex column jcc aic c-pointer ${componentLibraryIsOpen ? 'selectedSection' : ''} `} style={{ height: 100 }} onClick={() => {
									this.toggleSection('library');
								}}>
									<i className='las la-book mb-10' style={{ fontSize: 30 }}></i>
									<h5>Library</h5></div>
								<div className={`flex column jcc aic c-pointer ${this.state.historyOpen ? 'selectedSection' : ''} `} style={{ height: 100 }} onClick={() => {
									this.toggleSection('history');
								}}>
									<i className='las la-history mb-10' style={{ fontSize: 30 }}></i>
									<h5>History</h5></div>
							</div>
						</div>
					</div>
				</DragDropContext>
				{
					addCustomFieldModalOpen && (
						<AddCustomFieldModal
							adding={true}
							toggle={() => this.setState({ addCustomFieldModalOpen: false, focusedField: null })}
							fields={this.props.fields}
							section="attendees"
							addField={this.addField}
							updateCustomFields={this.props.updateCustomFields}
							refreshCustomFields={this.props.refreshCustomFields}
							eventId={this.props.eventId}
							fieldName={this.state.focusedField.label}
							orgId={this.props.orgId}
							programId={this.props.programId}
						/>
					)
				}
				{
					this.state.uploadOptionsModalOpen && (
						<UploadOptionsModal
							isOpen={this.state.uploadOptionsModalOpen}
							toggle={() => this.setState({
								uploadOptionsModalOpen: false,
								uploadTargetSection: null,
								uploadTargetField: null
							})}
							onUpload={this.handleUploadOptions}
						/>
					)
				}
			</Fragment >
		);
	}
}

export default FormBuilder;

const groupStyles = {
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between'
};
const groupBadgeStyles = {
	backgroundColor: '#EBECF0',
	borderRadius: '2em',
	color: '#172B4D',
	display: 'inline-block',
	fontSize: 12,
	fontWeight: 'normal',
	lineHeight: '1',
	minWidth: 1,
	padding: '0.16666666666667em 0.5em',
	textAlign: 'center'
};
